/**
 * Event Detail Page
 * Shows event details with tabs: Overview, Attendees, Call to Action, Vouchers, Campaigns, Tasks, Lists, Comments, Team
 */

import React, { useState } from "react";
import {
  useParams,
  useNavigate,
  useSearchParams,
  Link,
} from "react-router-dom";
import { isSafeInternalPath } from "../utils/navigation";
import {
  eibAudienceLabel,
  isEibAudienceLink,
  segmentHref,
  sortEventListRows,
} from "../utils/eibAudienceLink";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useQuery } from "@tanstack/react-query";
import {
  getAttendanceSummary,
  getAttendanceRosterPage,
} from "../services/attendanceReport.service";
// ONE canonical attendance contract for every screen (see utils/eventAttendance.ts).
// The cohort sets and the attended arithmetic live there, not here.
import {
  type AttendanceCohort,
  ADVANCE_REGISTRATION_COHORTS,
  PRESENT_COHORTS,
  canonicalAttendance,
} from "../utils/eventAttendance";
import {
  getEventById,
  getEventAttendees,
  checkInAttendee,
  updateAttendeeStatus,
  getEventVouchers,
  getEventComments,
  createEventComment,
  updateEventComment,
  deleteEventComment,
  duplicateEvent,
  duplicateEventToDates,
  isEventPartialSaveError,
  getEventListAssociations,
  buildOperatorAddedAttendeeRow,
  OPERATOR_ADDED_ATTENDANCE_STATUS,
  type EventComment,
} from "../services/events.service";
import type { StaffingCopyFailure } from "../services/eventDateModel.service";
import { formatDate, formatTime } from "../utils/formatters";
import { exportToExcel } from "../utils/dataImportExport";
import {
  registrantRowToExport,
  REGISTRANT_EXPORT_COLUMNS,
} from "../components/events/registrantsExport";
import CheckInCountsModal from "../components/events/CheckInCountsModal";
import { EVENT_TYPES, VISIBLE_EVENT_TYPES } from "../utils/constants";
import { featureFlags } from "../utils/featureFlags";
import {
  getDateFromISO,
  getTimeFromISO,
  formatEventTime,
  formatEventDateRange,
} from "../utils/eventTime";
import {
  eventStatusChangeBlockReason,
  showsSetupIncomplete,
  eventTypeIsNoPromo,
} from "../utils/eventTypePolicy";
import Button from "../components/common/Button";
import TimezoneSelector, {
  DEFAULT_TIMEZONE,
  getTimezoneOffset,
  US_TIMEZONES,
} from "../components/common/TimezoneSelector";
import LoadingSpinner from "../components/common/LoadingSpinner";
import { supabase } from "../services/supabase";
import { fetchAllRows, pageByNameThenId } from "../utils/fetchAllRows";
import {
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  DocumentIcon,
  MegaphoneIcon,
  ClipboardDocumentListIcon,
  TicketIcon,
  PencilIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserPlusIcon,
  FunnelIcon,
  ChatBubbleLeftIcon,
  PaperAirplaneIcon,
  TrashIcon,
  UserCircleIcon,
  ExclamationTriangleIcon,
  ShareIcon,
  DocumentDuplicateIcon,
  QrCodeIcon,
  ArrowRightIcon,
  GlobeAltIcon,
  GiftIcon,
  XMarkIcon,
  BanknotesIcon,
} from "@heroicons/react/24/outline";
import { CheckCircleIcon as CheckCircleIconSolid } from "@heroicons/react/24/solid";
import type { Event, EventStatus } from "../types/event.types";
import {
  CANCELLATION_REASONS,
  CLASS_DELIVERY_METHODS,
  USLS_CURRICULUM_OPTIONS,
  CLASS_SUBTYPES,
  EVENT_OWNERSHIP_OPTIONS,
  VOUCHER_USED_EVENT_TYPES,
} from "../types/event.types";
import CancellationReasonModal from "../components/events/CancellationReasonModal";
import DuplicateEventDialog from "../components/events/DuplicateEventDialog";
import { useAuth } from "../contexts/AuthContext";
import {
  completedOptionState,
  completionRefusalMessage,
} from "../utils/eventCompletePermission";
import { useToast } from "../components/ui/Toast";
import {
  updateEvent,
  cancelEvent,
  getEventModifyRights,
} from "../services/events.service";
import { useQueryClient } from "@tanstack/react-query";
import { eventKeys } from "../hooks/useEvents";
import SharingTab from "../components/events/tabs/SharingTab";
import SalesRepsTab from "../components/events/tabs/SalesRepsTab";
import TimeOffConflictBanner from "../components/events/TimeOffConflictBanner";
import ReportsTab from "../components/events/tabs/ReportsTab";
import { getEventReportSchedules } from "../services/reports.service";
import OverviewTab from "../components/events/tabs/OverviewTab";
import EditSalesHierarchyDialog from "../components/events/EditSalesHierarchyDialog";
import VenueCheckinToggle from "../components/events/VenueCheckinToggle";
import {
  FIELD_MANAGER_HIDDEN_EVENT_TABS,
  FIELD_MANAGER_HIDDEN_EVENT_TYPES,
  FIELD_MANAGER_READ_ONLY_EVENT_TABS,
  isFieldManagerRole,
} from "../utils/fieldManagerAccess";
import { EventDaysStatusPanel } from "../components/events/EventDayRun";
import {
  EventDailyHoursList,
  EventDailyHoursNotice,
} from "../components/events/EventDailyHours";
import {
  dailyHoursApplicabilityLocal,
  resolveDailyHours,
} from "../utils/eventDayHours";
import EventDailyHoursEditor, {
  initialDailyHoursValue,
  UNLOADED_DAILY_HOURS,
  planDayHoursSave,
  type DailyHoursValue,
} from "../components/events/EventDailyHoursEditor";
import {
  canEditEventDayHours,
  describeDayHoursError,
  getEventDayHoursForEvent,
  setEventDayHours,
} from "../services/eventDayHours.service";
import { eventDayHoursKeys } from "../hooks/useEventDayHours";
import EventOperationsTab from "../components/events/tabs/EventOperationsTab";
import CallToActionTab from "../components/events/tabs/CallToActionTab";
import EventHostCTAsPanel, {
  eventHostCTAKeys,
} from "../components/events/EventHostCTAsPanel";
import { getEventHostCTAs } from "../services/eventHostCTAs.service";
import CheckinsTab from "../components/events/tabs/CheckinsTab";
import AttendanceReportView from "../components/events/AttendanceReportView";
import TicketsTab from "../components/events/tabs/TicketsTab";
import LandingPageTab from "../components/events/tabs/LandingPageTab";
import RewardsTab from "../components/events/tabs/RewardsTab";
import MemberVerificationTab from "../components/events/tabs/MemberVerificationTab";
import DrawingTab from "../components/events/tabs/DrawingTab";
import AttendeeQRCodeModal from "../components/events/AttendeeQRCodeModal";
import AttributionOverridesSection from "../components/events/AttributionOverridesSection";
import {
  getEventCTAs,
  regenerateAllEventCTAQRs,
  regenerateQRsForEvents,
  regenerateGenericCTAQRs,
} from "../services/eventCTA.service";
import {
  getContactByEmail,
  getRecentVouchersForContact,
} from "../services/contacts.service";
import {
  logVoucherIssued,
  logOfferLinkShared,
  logPhysicalDistributed,
  recordRewardActivity,
} from "../services/interactions.service";
import type { Contact } from "../types/contact.types";
import { voucherStatusLabel } from "../services/redemptions.service";
import EventInABoxWizard from "../components/events/EventInABoxWizard";
import EventWalkinPanel from "../components/events/EventWalkinPanel";
import EventCampaignsTab from "../components/events/tabs/EventCampaignsTab";
import BudgetExpensesTab from "../components/events/tabs/BudgetExpensesTab";
import { useFinanceAccess } from "../hooks/useFinanceAccess";
import EventStatusBadge from "../components/events/EventStatusBadge";

type TabType =
  | "overview"
  | "operations"
  | "attendees"
  | "tickets"
  | "cta"
  | "landing-page"
  | "rewards"
  | "documents"
  | "campaigns"
  | "tasks"
  | "vouchers"
  | "lists"
  | "sales-reps"
  | "sharing"
  | "reports"
  | "verification"
  | "drawing"
  | "finance";

// Types for event associations
interface EventListLink {
  id: string;
  list_id: string;
  association_type: string;
  // Set when an Event in a Box launch created the link (migration 894): read-only here.
  eib_launch_id?: string | null;
  lists?: {
    id: string;
    name: string;
    list_type?: string | null;
    contact_count?: number | null;
  };
}

interface EventAttendee {
  id: string;
  first_name: string;
  last_name: string;
  email?: string;
  contact_email?: string;
  display_name?: string;
  checkin_code?: string;
  checked_in_at?: string;
  status?: string;
  party_member_count?: number;
  party_nonmember_count?: number;
  attended_member_count?: number;
  attended_nonmember_count?: number;
  // Guest-invite: set when this attendee was invited as someone's guest; `inviter` is the embedded
  // inviter contact (first/last name) for the "guest of <inviter>" label.
  invited_by_contact_id?: string | null;
  inviter?: { first_name?: string | null; last_name?: string | null } | null;
}

interface ListItem {
  id: string;
  name: string;
  description?: string;
}

/**
 * The EXACT set of canonical attendance query keys a walk-in (or any attendee mutation) must
 * invalidate so every view updates together: the raw attendee list, the summary CARD, the cohort
 * TAB, and the Operations roster. Exported as the single source of truth shared by the onRegistered
 * handler and the parity regression test — so a wrong-key regression in the handler breaks the test
 * (re-audit-5: the test previously invalidated ALL queries and could not catch that). Order/shape
 * here is asserted directly in EventDetail.attendeesParity.test.tsx.
 */
export const attendanceInvalidationKeys = (
  eventId: string,
): (readonly [string, string])[] => [
  ["event-attendees", eventId],
  ["event-attendance-summary", eventId],
  ["event-attendance-cohorts", eventId],
  ["event-operations-roster", eventId],
];

const EventDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabType>("overview");
  // mig 1279 "Start advertising" (declared before the loading / error returns: hooks run on every render)
  const [startingAdvertising, setStartingAdvertising] = useState(false);
  // round 19 P2-B: Start advertising needs the right to edit the event (server: _caller_may_start_advertising); an RM / TM
  // who can open the event has it in scope
  const { data: modifyRights } = useQuery({
    queryKey: ["event-modify-rights", id],
    queryFn: () => getEventModifyRights(id ? [id] : []),
    enabled: !!id,
    staleTime: 60_000,
  });
  // Regional / Territory Managers (owner 2026-10-29, REQ-2.1/2.2): they view and run events but never change an
  // event's setup; five tabs are hidden and the setup tabs are view-only. The database refuses the writes too.
  const fieldManager = isFieldManagerRole(user?.role);
  const canConfigure = !fieldManager;
  const [showHierarchyEditor, setShowHierarchyEditor] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  // R7 (2026-05-25): preserve the operator's list-page state on back-nav.
  // backTo carries the encoded pathname+search from the Events list (or
  // any other caller); validated via isSafeInternalPath to close the
  // open-redirect gap that existed in the original CampaignDetail
  // backTo pattern (no validation, raw navigate(rawBackTo)).
  const [searchParams, setSearchParams] = useSearchParams();

  // Campaign detail links can return directly to this tab. Keep the scope
  // deliberately narrow so older/unknown tab query values cannot expose a tab
  // the current user or feature flags would otherwise hide.
  React.useEffect(() => {
    if (searchParams.get("tab") === "campaigns") {
      setActiveTab("campaigns");
    }
    // mig 1360: the Events page "waiting for a promoter / host CTA" list links straight to the Call to Action tab.
    if (searchParams.get("tab") === "cta") {
      setActiveTab("cta");
    }
  }, [searchParams]);
  React.useEffect(() => {
    if (fieldManager && FIELD_MANAGER_HIDDEN_EVENT_TABS.has(activeTab)) {
      setActiveTab("overview");
    }
  }, [fieldManager, activeTab]);
  const rawBackTo = searchParams.get("backTo");
  const backTo =
    isSafeInternalPath(rawBackTo) && rawBackTo ? rawBackTo : "/events";

  // Inline editing states
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [isEditingType, setIsEditingType] = useState(false);
  const [editType, setEditType] = useState("");
  const [isEditingDateTime, setIsEditingDateTime] = useState(false);
  const [editTimezone, setEditTimezone] = useState(DEFAULT_TIMEZONE);
  const [editDateTime, setEditDateTime] = useState({
    startDate: "",
    startTime: "",
    endDate: "",
    endTime: "",
    allDay: false,
  });
  const [isEditingLocation, setIsEditingLocation] = useState(false);
  const [editLocation, setEditLocation] = useState({
    locationType: "",
    locationName: "",
    addressLine1: "",
    city: "",
    state: "",
    postalCode: "",
    meetingUrl: "",
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  // Event in a Box — Slice 1 one-step setup wizard.
  const [showEibWizard, setShowEibWizard] = useState(false);
  const [showWalkin, setShowWalkin] = useState(false);
  // Sprint A Batch 4 A10.3 — auto-QR progress on multi-date duplication (CFG-02: the merged
  // Duplicate dialog owns the dates and the auto-QR choice).
  const [autoQRProgress, setAutoQRProgress] = useState<{
    current: number;
    total: number;
  } | null>(null);
  const [regenerating, setRegenerating] = useState(false);
  const [regenResult, setRegenResult] = useState<{
    regenerated: number;
    failed: number;
    skipped: number;
  } | null>(null);
  const [showCancellationModal, setShowCancellationModal] = useState(false);

  // Fetch event details
  const {
    data: event,
    isLoading,
    error,
    refetch: refetchEvent,
  } = useQuery({
    queryKey: eventKeys.detail(id!),
    queryFn: () => getEventById(id!),
    enabled: !!id,
  });

  // Arriving from Create with ?setup=1 (capability-rich events) auto-opens the
  // Event-in-a-Box guided setup once the event has loaded. Fires once.
  const setupAutoOpenedRef = React.useRef(false);
  const [eibInitialMode, setEibInitialMode] = useState<
    "show_up" | "pre_register" | undefined
  >(undefined);
  React.useEffect(() => {
    if (
      !setupAutoOpenedRef.current &&
      searchParams.get("setup") === "1" &&
      event &&
      canConfigure && // RM/TM never open the Event in a Box setup (internal audit round 2 P3)
      !eventTypeIsNoPromo(event.type)
    ) {
      setupAutoOpenedRef.current = true;
      // Capture the pre-selected workflow BEFORE stripping the params so the wizard
      // opens on the right mode (show_up for conversion/walk-up, pre_register otherwise).
      const m = searchParams.get("mode");
      setEibInitialMode(
        m === "show_up" || m === "pre_register" ? m : undefined,
      );
      setShowEibWizard(true);
      // Consume the trigger so a later remount (e.g. browser-back) doesn't re-open
      // the wizard. Preserve any other params.
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("setup");
          next.delete("mode");
          return next;
        },
        { replace: true },
      );
    }
  }, [searchParams, event, setSearchParams, canConfigure]);

  // Walk-in / booth-scan are EVENT-SCOPED server-side (can_operate_event_reward): an active
  // admin/superadmin, the event's assigned MSR, or a manager/staff/co-rep-MSR with an
  // event_permissions grant. Gate the +Walk-in button on the SAME predicate (per-event, not
  // per-role) so an ungranted manager never sees a button that then 403s. Declared here (before any
  // early return) to keep hook order stable.
  // Fail-closed AND self-revalidating (audit F4): a useQuery so the grant is re-checked on window
  // focus + periodic staleness, not frozen at mount. If a grant is revoked while the page is open,
  // returning to the tab re-resolves it to false and the operator surface disappears. Defaults false
  // (hidden) until THIS event's authz resolves; any RPC error resolves to false.
  const { data: canWalkIn = false } = useQuery({
    queryKey: ["can-operate-event", event?.id, user?.id],
    queryFn: async () => {
      if (!event?.id || !user?.id) {
        return false;
      }
      const { data, error } = await supabase.rpc("can_operate_event_reward", {
        p_event_id: event.id,
        p_actor_id: user.id,
      });
      return !error && data === true;
    },
    enabled: !!event?.id && !!user?.id,
    refetchOnWindowFocus: true,
    // staleTime alone only MARKS data stale; it does not schedule a fetch. refetchInterval actively
    // re-resolves the grant on a cadence so a revocation is caught even on a continuously-open page
    // that never blurs/remounts (audit re-iter finding 2).
    staleTime: 30000,
    refetchInterval: 60000,
  });
  // Finance Budget & Expenses tab: shown only when the flag is on AND the server says the finance module is
  // enabled and this person has finance access to THIS event (finance_my_access). Loading/error = hidden.
  const { access: financeAccess } = useFinanceAccess(id ?? null);
  // 978: FIELD profile -- anyone associated with THIS event (creator, organizer, Sales Reps list, Team edit/owner),
  // finance admins, and (1140, setting on) every Admin. Never the MSR role (1140). The server's event_access is the answer.
  const showFinanceTab =
    !!financeAccess?.enabled && financeAccess.event_access === true;
  React.useEffect(() => {
    if (!showFinanceTab) {
      setActiveTab((t) => (t === "finance" ? "overview" : t));
    }
  }, [showFinanceTab]);

  // When authorization drops (revoked mid-session), tear down any operator UI still on screen.
  React.useEffect(() => {
    if (!canWalkIn) {
      setShowWalkin(false);
      setActiveTab((t) => (t === "operations" ? "overview" : t));
    }
  }, [canWalkIn]);

  // Owner 2026-10-29: a class, table, seminar or speaking engagement sends no emails and has no landing page, so it
  // has no Campaigns, Landing Page or Lists tab (and no Event in a Box setup). A deep link (?tab=campaigns) or a
  // type change while one of those is open falls back to Overview. The server refuses the same writes (mig 1260).
  const noPromo = eventTypeIsNoPromo(event?.type);
  React.useEffect(() => {
    if (noPromo) {
      setActiveTab((t) =>
        t === "campaigns" || t === "landing-page" || t === "lists"
          ? "overview"
          : t,
      );
      setShowEibWizard(false);
    }
  }, [noPromo, activeTab]);

  // Hours each day of a multi-day event (migration 2026-10-27-1120). ONE read of every row saved for this event,
  // used by the header display and by the inline editor. `eventDayHoursRows` is undefined until the read SUCCEEDS
  // ([] = loaded, none saved); a failed read stays undefined and is reported (external audit 705a4bd9 F1).
  const dayHoursQuery = useQuery({
    queryKey: [...eventDayHoursKeys.all, "event", id],
    queryFn: () => getEventDayHoursForEvent(id!),
    enabled: !!id,
  });
  const eventDayHoursRows = dayHoursQuery.isSuccess
    ? dayHoursQuery.data
    : undefined;
  // Fresh = succeeded and not re-fetching (after a save invalidates it): the editor draft is only ever built from this.
  const eventDayHoursFresh =
    dayHoursQuery.isSuccess && !dayHoursQuery.isFetching
      ? dayHoursQuery.data
      : undefined;
  const eventDailyHours = event
    ? resolveDailyHours(event, eventDayHoursRows)
    : null;

  // Inline "Edit Date & Time": the hours-each-day section (saved after the dates). The draft starts UNLOADED and is
  // initialized exactly once per editing session, from the first successful read; a later refetch never replaces it
  // (the operator's edits stay), and an uninitialized draft never writes.
  const [editDailyHours, setEditDailyHours] =
    useState<DailyHoursValue>(UNLOADED_DAILY_HOURS);
  React.useEffect(() => {
    if (
      isEditingDateTime &&
      !editDailyHours.initialized &&
      eventDayHoursFresh !== undefined
    ) {
      setEditDailyHours(initialDailyHoursValue(eventDayHoursFresh));
    }
  }, [isEditingDateTime, editDailyHours.initialized, eventDayHoursFresh]);
  const dayHoursLoadError = dayHoursQuery.isError
    ? describeDayHoursError(dayHoursQuery.error)
    : null;
  const { data: canEditDayHours, isError: canEditDayHoursFailed } = useQuery({
    queryKey: ["event-day-hours", "can-edit", id],
    queryFn: () => canEditEventDayHours(id!),
    enabled: !!id && isEditingDateTime,
    staleTime: 0,
  });
  const editDailyHoursApp = React.useMemo(() => {
    const tz = editTimezone || DEFAULT_TIMEZONE;
    // A blank end date leaves the stored end unchanged (saveDateTime sends no end_time), so read it from the event.
    const keepEnd = !editDateTime.endDate && !!event?.end_time;
    return dailyHoursApplicabilityLocal({
      startDate: editDateTime.startDate,
      startTime: editDateTime.startTime,
      endDate: keepEnd
        ? getDateFromISO(event!.end_time, tz)
        : editDateTime.endDate,
      endTime: keepEnd
        ? getTimeFromISO(event!.end_time, tz)
        : editDateTime.endTime,
      allDay: editDateTime.allDay,
      dateTbd: false,
      timezone: tz,
    });
  }, [editDateTime, editTimezone, event]);
  const editDailyHoursReadOnly =
    canEditDayHours === false
      ? "Only the event's owner, an editor of the event, or an admin can change the hours for each day."
      : canEditDayHoursFailed
        ? "Could not check who can change the hours for each day; they are left unchanged. Reload to try again."
        : canEditDayHours === undefined
          ? "Checking who can change the hours for each day..."
          : null;

  // Fetch CTAs for tab badge count and duplicate modal
  const { data: eventCTAs = [] } = useQuery({
    queryKey: ["event-ctas-list", id],
    queryFn: () => getEventCTAs(id!),
    enabled: !!id,
    staleTime: 30000,
  });
  // Owner 2026-10-29: a class, table, seminar or speaking engagement shows its venue's / host organization's CTAs
  // instead of its own (switched off), so the Calls to Action badge counts those (same query as the tab's panel).
  const { data: hostCTAs = [] } = useQuery({
    queryKey: eventHostCTAKeys.list(id ?? ""),
    queryFn: () => getEventHostCTAs(id!),
    enabled: !!id && noPromo,
    staleTime: 30000,
  });
  const ctaCount = noPromo ? hostCTAs.length : eventCTAs.length;

  const { data: taskCount = 0 } = useQuery({
    queryKey: ["event-tasks-count", id],
    queryFn: async () => {
      const { count } = await supabase
        .from("tasks")
        .select("*", { count: "exact", head: true })
        .eq("event_id", id!)
        .in("status", ["to_do", "in_progress"]);
      return count || 0;
    },
    enabled: !!id,
    staleTime: 30000,
  });

  const { data: commentCount = 0 } = useQuery({
    queryKey: ["event-comments-count", id],
    queryFn: async () => {
      const comments = await getEventComments(id!);
      return comments.length;
    },
    enabled: !!id,
    staleTime: 30000,
  });

  const { data: reportScheduleCount = 0 } = useQuery({
    queryKey: ["report-schedules-count", id],
    queryFn: async () => {
      const schedules = await getEventReportSchedules(id!);
      return schedules.filter((s) => s.is_active && !s.last_sent_at).length;
    },
    enabled: !!id,
    staleTime: 30000,
  });

  // R1+R2 post-ship fix (2026-05-17): read the DENORMALIZED hierarchy
  // fields that EventForm saves, not the buggy old "climb the rep's
  // parent chain" path. Post-ship audit caught that EventDetail was
  // still on the old logic even after R2A "fixed" it in EventForm —
  // the fix only landed on the write path, not the display path.
  //
  // The four position_id columns on events are populated by EventForm's
  // hierarchyChain state which comes from derive_account_hierarchy
  // (account-owner chain, not rep's manager chain). Resolve each id to
  // a display name via a single join.
  const hierarchyIds = [
    event?.sales_position_id,
    event?.territory_manager_position_id,
    event?.regional_manager_position_id,
    event?.sales_director_position_id,
  ].filter((id): id is string => !!id);

  const { data: salesHierarchy } = useQuery({
    queryKey: ["event-sales-hierarchy", ...hierarchyIds],
    queryFn: async () => {
      if (hierarchyIds.length === 0) {
        return null;
      }
      const { data } = await supabase
        .from("sales_positions")
        .select("id, title, assigned_name, is_active")
        .in("id", hierarchyIds);
      const byId = new Map(
        (data ?? []).map((p) => [
          p.id,
          (p.assigned_name || p.title) +
            (p.is_active === false ? " (inactive)" : ""),
        ]),
      );
      return {
        repName: event?.sales_position_id
          ? (byId.get(event.sales_position_id) ?? null)
          : null,
        tm: event?.territory_manager_position_id
          ? (byId.get(event.territory_manager_position_id) ?? null)
          : null,
        rm: event?.regional_manager_position_id
          ? (byId.get(event.regional_manager_position_id) ?? null)
          : null,
        sd: event?.sales_director_position_id
          ? (byId.get(event.sales_director_position_id) ?? null)
          : null,
      };
    },
    enabled: hierarchyIds.length > 0,
    staleTime: 60000,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-semibold">Error loading event</h3>
          <p className="text-red-600 text-sm mt-1">
            {error instanceof Error ? error.message : "Event not found"}
          </p>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate(backTo)}
            className="mt-4"
          >
            Back to Events
          </Button>
        </div>
      </div>
    );
  }

  const allTabs = [
    {
      id: "overview" as TabType,
      label: "Overview",
      icon: CalendarIcon,
      badge: 0,
      badgeColor: "",
    },
    // Operator surface — only for operators of THIS event (can_operate_event_reward). Live roster +
    // walk-in + reward fix; the mobile "run the event" tab.
    ...(canWalkIn
      ? [
          {
            id: "operations" as TabType,
            label: "Operations",
            icon: UserGroupIcon,
            badge: 0,
            badgeColor: "",
          },
        ]
      : []),
    {
      id: "attendees" as TabType,
      label: "Attendees",
      icon: UserGroupIcon,
      badge: 0,
      badgeColor: "",
    },
    // Ticketing isn't deployed to production yet (event_ticket_tiers absent),
    // so the Tickets tab errors there. Hide it unless the feature flag is on.
    ...(featureFlags.eventTicketing
      ? [
          {
            id: "tickets" as TabType,
            label: "Tickets",
            icon: TicketIcon,
            badge: 0,
            badgeColor: "",
          },
        ]
      : []),
    {
      id: "cta" as TabType,
      label: "Calls to Action",
      icon: QrCodeIcon,
      badge: ctaCount,
      badgeColor: "bg-green-500",
    },
    {
      id: "landing-page" as TabType,
      label: "Landing Page",
      icon: GlobeAltIcon,
      badge: 0,
      badgeColor: "",
    },
    {
      id: "rewards" as TabType,
      label: "Rewards",
      icon: GiftIcon,
      badge: 0,
      badgeColor: "",
    },
    {
      id: "drawing" as TabType,
      label: "Drawing",
      icon: GiftIcon,
      badge: 0,
      badgeColor: "",
    },
    {
      id: "vouchers" as TabType,
      label: "Vouchers",
      icon: TicketIcon,
      badge: 0,
      badgeColor: "",
    },
    {
      id: "campaigns" as TabType,
      label: "Campaigns",
      icon: MegaphoneIcon,
      badge: 0,
      badgeColor: "",
    },
    {
      id: "tasks" as TabType,
      label: "Tasks",
      icon: ClipboardDocumentListIcon,
      badge: taskCount,
      badgeColor: "bg-red-500",
    },
    {
      id: "lists" as TabType,
      label: "Lists",
      icon: ClipboardDocumentListIcon,
      badge: 0,
      badgeColor: "",
    },
    {
      id: "documents" as TabType,
      label: "Comments",
      icon: ChatBubbleLeftIcon,
      badge: commentCount,
      badgeColor: "bg-yellow-500",
    },
    {
      id: "sales-reps" as TabType,
      // GS-05: the team section is "Confirm schedule" (the Sales Reps list + who works each day)
      label: "Confirm Schedule",
      icon: UserGroupIcon,
      badge: 0,
      badgeColor: "",
    },
    {
      id: "verification" as TabType,
      label: "Verification",
      icon: UserGroupIcon,
      badge: 0,
      badgeColor: "",
    },
    {
      id: "sharing" as TabType,
      label: "Team",
      icon: ShareIcon,
      badge: 0,
      badgeColor: "",
    },
    {
      id: "reports" as TabType,
      label: "Reports",
      icon: DocumentIcon,
      badge: reportScheduleCount,
      badgeColor: "bg-blue-500",
    },
    ...(showFinanceTab
      ? [
          {
            id: "finance" as TabType,
            label: "Budget & Expenses",
            icon: BanknotesIcon,
            badge: 0,
            badgeColor: "",
          },
        ]
      : []),
  ];
  // Owner 2026-10-29: no Campaigns, Landing Page or Lists tab for a class, table, seminar or speaking engagement.
  const NO_PROMO_HIDDEN_TABS: ReadonlyArray<TabType> = [
    "campaigns",
    "landing-page",
    "lists",
  ];
  // both rules apply: RM/TM hidden tabs, and the no-promo types' hidden tabs
  const tabs = allTabs.filter(
    (t) =>
      !(fieldManager && FIELD_MANAGER_HIDDEN_EVENT_TABS.has(t.id)) &&
      !(noPromo && NO_PROMO_HIDDEN_TABS.includes(t.id)),
  );

  // Helper to safely get location address object
  const getLocationAddress = () => {
    if (!event.location_address) {
      return null;
    }
    // Handle case where location_address might be a string (shouldn't happen but be safe)
    if (typeof event.location_address === "string") {
      console.warn(
        "[EventDetail] location_address is a string:",
        event.location_address,
      );
      return null;
    }
    return event.location_address;
  };

  // Create Google Maps link from location_address
  const getGoogleMapsLink = () => {
    const address = getLocationAddress();
    if (!address) {
      return null;
    }
    const { address_line1, city, state, postal_code } = address;
    const addressParts = [address_line1, city, state, postal_code].filter(
      Boolean,
    );
    if (addressParts.length === 0) {
      return null;
    }
    const query = encodeURIComponent(addressParts.join(", "));
    return `https://www.google.com/maps/search/?api=1&query=${query}`;
  };

  const googleMapsLink = getGoogleMapsLink();

  // Handle status change
  const handleStatusChange = async (newStatus: EventStatus) => {
    if (!id || newStatus === event.status) {
      return;
    }

    // Intercept cancellation to collect reason
    if (newStatus === "cancelled") {
      setShowCancellationModal(true);
      return;
    }

    // CK17 (2026-06): for attendance-tracked event types (class / speaking
    // engagement / seminar) the attendance count is MANDATORY before the event
    // can be marked Completed — block, don't just warn. Shared gate so the edit
    // form (EventForm) enforces the identical rule and can't bypass it.
    const blockReason = eventStatusChangeBlockReason(
      newStatus,
      event.type,
      event.attendance_count,
      event.start_time, // SA-2: block completing a future-dated event
    );
    if (blockReason) {
      showToast("error", blockReason);
      return;
    }

    setIsUpdatingStatus(true);
    try {
      await updateEvent(id, { status: newStatus });
      queryClient.invalidateQueries({ queryKey: eventKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: eventKeys.lists() });
      showToast("success", `Event status updated to ${newStatus}`);
    } catch (error) {
      console.error("Error updating status:", error);
      showToast(
        "error",
        completionRefusalMessage(error) ?? "Failed to update event status",
      );
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Clear the "setup incomplete" flag (from "Save & finish later" in the wizard)
  // once the operator has finished setting the event up.
  // mig 1279 (owner 2026-10-30): "Start advertising" turns a Gun Show - Not Advertised into an advertised gun show
  // (Configure Event / Event in a Box setup shows again). Whoever may edit the event, or an RM / TM in scope; the server
  // refuses a deleted / cancelled / completed / finished show and anyone else.
  const canStartAdvertising =
    !!event?.not_advertised &&
    event?.type === "gun_show" &&
    (fieldManager || (!!id && modifyRights?.[id] === true));
  const startAdvertising = async () => {
    if (!id) {
      return;
    }
    if (
      !window.confirm(
        "Start advertising this gun show? It becomes a regular gun show: Event in a Box invitations and a landing page can be set up (Configure Event), and it counts in close-rate and Sales by Type reports.",
      )
    ) {
      return;
    }
    setStartingAdvertising(true);
    try {
      const { error } = await supabase.rpc("start_advertising_event", {
        p_event_id: id,
      });
      if (error) {
        throw error;
      }
      queryClient.invalidateQueries({ queryKey: eventKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: eventKeys.lists() });
      showToast(
        "success",
        "Advertising started. Configure Event is available to set up invitations.",
      );
    } catch (e) {
      const msg = (e as { message?: string })?.message ?? "";
      showToast(
        "error",
        msg.replace(/^[A-Z_]+:\s*/, "") ||
          "Couldn't start advertising this gun show.",
      );
    } finally {
      setStartingAdvertising(false);
    }
  };

  const markSetupComplete = async () => {
    if (!id) {
      return;
    }
    try {
      await updateEvent(id, { setup_incomplete: false });
      queryClient.invalidateQueries({ queryKey: eventKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: eventKeys.lists() });
      showToast(
        "success",
        "Marked as set up — the Incomplete badge is removed.",
      );
    } catch {
      showToast("error", "Couldn't mark as set up.");
    }
  };

  const handleCancellationConfirm = async (reason: string, notes: string) => {
    if (!id) {
      return;
    }
    setIsUpdatingStatus(true);
    try {
      await cancelEvent(id, reason, notes);
      queryClient.invalidateQueries({ queryKey: eventKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: eventKeys.lists() });
      showToast("success", "Event cancelled");
      setShowCancellationModal(false);
    } catch (error) {
      console.error("Error cancelling event:", error);
      showToast("error", "Failed to cancel event");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Date/time helpers are imported from ../utils/eventTime. The local
  // naive split versions were removed 2026-06-03 (Sprint A Batch 1 A1)
  // — they ignored the stored timezone and combined with the missing
  // save-time validation produced the "saved 2 PM, reopens as 12 AM"
  // reproduction Heather flagged in the R1 review.

  // 2026-06-07 post-deploy: lock all inline editors when event
  // is cancelled (Heather R1 / her "should also be disabled and
  // no longer editable" callout). Status dropdown was already
  // gated separately; this guard catches title / type / date /
  // location / rep / voucher-flags. Comments and cancellation
  // reason/notes intentionally stay editable so the team can
  // continue documenting the cancellation.
  const isCancelled = event?.status === "cancelled";

  // Start editing title
  const startEditingTitle = () => {
    if (isCancelled || !canConfigure) {
      return;
    }
    setEditTitle(event.title);
    setIsEditingTitle(true);
  };

  // Save title
  const saveTitle = async () => {
    if (!editTitle.trim() || editTitle === event.title) {
      setIsEditingTitle(false);
      return;
    }
    setIsSaving(true);
    try {
      await updateEvent(id!, { title: editTitle.trim() });
      queryClient.invalidateQueries({ queryKey: eventKeys.detail(id!) });
      queryClient.invalidateQueries({ queryKey: eventKeys.lists() });
      showToast("success", "Title updated");
      setIsEditingTitle(false);
    } catch (error) {
      showToast("error", "Failed to update title");
    } finally {
      setIsSaving(false);
    }
  };

  // Start editing type
  const startEditingType = () => {
    if (isCancelled || !canConfigure) {
      return;
    }
    setEditType(event.type || "");
    setIsEditingType(true);
  };

  // Save type
  const saveType = async () => {
    if (!editType) {
      showToast("error", "Please select an event type");
      return;
    }
    if (editType === event.type) {
      setIsEditingType(false);
      return;
    }
    setIsSaving(true);
    try {
      // "Not advertised" exists only on a gun show (migration 1242 CHECK): leaving gun show clears it.
      await updateEvent(id!, {
        type: editType as Event["type"],
        ...(event.not_advertised && editType !== "gun_show"
          ? { not_advertised: false }
          : {}),
      });
      queryClient.invalidateQueries({ queryKey: eventKeys.detail(id!) });
      queryClient.invalidateQueries({ queryKey: eventKeys.lists() });
      showToast("success", "Event type updated");
      setIsEditingType(false);
    } catch (error) {
      console.error("Error updating type:", error);
      showToast("error", "Failed to update type");
    } finally {
      setIsSaving(false);
    }
  };

  // Toggle voucher/curriculum used flags
  const saveVoucherFlags = async (
    field: "voucher_used" | "usls_curriculum_used",
    value: boolean,
  ) => {
    if (isCancelled) {
      return;
    }
    try {
      await updateEvent(id!, { [field]: value } as any);
      queryClient.invalidateQueries({ queryKey: eventKeys.detail(id!) });
    } catch (error) {
      showToast("error", "Failed to update");
    }
  };

  // Start editing date/time. Reads through the timezone-aware helpers
  // so the displayed values match what the operator saved, even if the
  // stored ISO string is normalized to UTC.
  const startEditingDateTime = () => {
    if (isCancelled || !canConfigure) {
      return;
    }
    const tz = event.timezone || DEFAULT_TIMEZONE;
    setEditDateTime({
      startDate: getDateFromISO(event.start_time, tz),
      startTime: getTimeFromISO(event.start_time, tz),
      endDate: getDateFromISO(event.end_time, tz),
      endTime: getTimeFromISO(event.end_time, tz),
      allDay: event.all_day || false,
    });
    setEditTimezone(tz);
    // Not freshly loaded (pending / refetching / failed) -> UNLOADED: disabled and never writes; the effect near the
    // hours query initializes it once when the read succeeds.
    setEditDailyHours(initialDailyHoursValue(eventDayHoursFresh));
    setIsEditingDateTime(true);
  };

  // Save date/time. Required-time validation added 2026-06-03 (Sprint A
  // Batch 1 A1, audit-AI GREEN). Mirrors the R1 guard in EventForm.tsx
  // which never made it to this inline-edit path. Skipped when
  // allDay=true — all-day events legitimately have no specific start
  // or end time.
  const saveDateTime = async () => {
    if (!editTimezone) {
      showToast("error", "Please select a time zone");
      return;
    }
    if (!editDateTime.allDay) {
      if (editDateTime.startDate && !editDateTime.startTime) {
        showToast("error", "Start time is required");
        return;
      }
      if (editDateTime.endDate && !editDateTime.endTime) {
        showToast("error", "End time is required");
        return;
      }
    }
    // Hours each day: refuse a blank or reversed day before anything is written.
    const dayHoursPlan = editDailyHoursReadOnly
      ? ({ action: "none" } as const)
      : planDayHoursSave(editDailyHoursApp, editDailyHours, eventDayHoursRows);
    if (dayHoursPlan.action === "invalid") {
      showToast("error", dayHoursPlan.message);
      return;
    }
    setIsSaving(true);
    try {
      const tzOffset = getTimezoneOffset(editTimezone, editDateTime.startDate);
      const endTzOffset = getTimezoneOffset(
        editTimezone,
        editDateTime.endDate || editDateTime.startDate,
      );
      // All-day events: anchor to local-midnight bounds; otherwise use
      // the validated wall-clock values directly.
      const start_time = editDateTime.allDay
        ? `${editDateTime.startDate}T00:00:00${tzOffset}`
        : `${editDateTime.startDate}T${editDateTime.startTime}:00${tzOffset}`;
      const end_time = editDateTime.endDate
        ? editDateTime.allDay
          ? `${editDateTime.endDate}T23:59:00${endTzOffset}`
          : `${editDateTime.endDate}T${editDateTime.endTime}:00${endTzOffset}`
        : undefined;

      // The backend rejects an end before the start, surfacing only a generic failure. Catch it
      // here with a clear message so the operator knows WHY (e.g. moving the start past a stale
      // end date — the common case behind "Failed to update date/time").
      if (end_time && new Date(end_time) < new Date(start_time)) {
        showToast("error", "The end date/time must be on or after the start.");
        return;
      }

      await updateEvent(id!, {
        start_time,
        end_time,
        all_day: editDateTime.allDay,
        timezone: editTimezone,
      });
      queryClient.invalidateQueries({ queryKey: eventKeys.detail(id!) });
      queryClient.invalidateQueries({ queryKey: eventKeys.lists() });
      queryClient.invalidateQueries({ queryKey: eventKeys.upcoming() });
      if (dayHoursPlan.action === "set" || dayHoursPlan.action === "clear") {
        try {
          const savedNow = await setEventDayHours(
            id!,
            dayHoursPlan.action === "set" ? dayHoursPlan.days : [],
          );
          // The RPC returns the whole set as stored: cache it so a re-opened editor starts from it.
          queryClient.setQueryData(
            [...eventDayHoursKeys.all, "event", id],
            savedNow,
          );
        } catch (dhErr) {
          showToast(
            "error",
            `Date/time saved, but the hours for each day were not: ${describeDayHoursError(dhErr)}`,
          );
        }
        queryClient.invalidateQueries({ queryKey: eventDayHoursKeys.all });
      }
      showToast("success", "Date/time updated");
      setIsEditingDateTime(false);
    } catch (error) {
      if (isEventPartialSaveError(error)) {
        // The dates committed; show what was saved and say what was not.
        queryClient.invalidateQueries({ queryKey: eventKeys.all });
        setIsEditingDateTime(false);
        showToast("error", error.message);
        return;
      }
      showToast(
        "error",
        error instanceof Error &&
          /^(Remove|That date change|This event is busy)/.test(error.message)
          ? error.message
          : "Failed to update date/time",
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Start editing location
  const startEditingLocation = () => {
    if (isCancelled || !canConfigure) {
      return;
    }
    setEditLocation({
      locationType: event.location_type || "physical",
      locationName: event.location_name || "",
      addressLine1: event.location_address?.address_line1 || "",
      city: event.location_address?.city || "",
      state: event.location_address?.state || "",
      postalCode: event.location_address?.postal_code || "",
      meetingUrl: event.meeting_url || "",
    });
    setIsEditingLocation(true);
  };

  // Save location
  const saveLocation = async () => {
    setIsSaving(true);
    try {
      // Build location_address if any address fields are present
      const hasAddressData =
        editLocation.addressLine1 ||
        editLocation.city ||
        editLocation.state ||
        editLocation.postalCode;
      const location_address = hasAddressData
        ? {
            address_line1: editLocation.addressLine1 || undefined,
            city: editLocation.city || undefined,
            state: editLocation.state || undefined,
            postal_code: editLocation.postalCode || undefined,
          }
        : undefined;

      await updateEvent(id!, {
        location_type: editLocation.locationType as Event["location_type"],
        location_name: editLocation.locationName || undefined,
        location_address,
        meeting_url: editLocation.meetingUrl || undefined,
      });
      queryClient.invalidateQueries({ queryKey: eventKeys.detail(id!) });
      queryClient.invalidateQueries({ queryKey: eventKeys.lists() });
      showToast("success", "Location updated");
      setIsEditingLocation(false);
    } catch (error) {
      showToast("error", "Failed to update location");
    } finally {
      setIsSaving(false);
    }
  };

  // CFG-02 (owner 2026-10-12): ONE Duplicate action. It always opens the dialog, which makes one
  // copy by default (CTA picker, pre-selected) or one scheduled copy per date the operator adds.
  const openDuplicateModal = () => {
    if (!id) {
      return;
    }
    setShowDuplicateModal(true);
  };

  // Multi-date duplicate handler (Sprint A Batch 2 Ship C)
  // Duplicates are created even when copying the original's Sales Reps fails (date-model events only); tell the
  // operator which copies need reps added, and why. Shown for 20 s.
  const reportStaffingCopyFailures = (
    failures: StaffingCopyFailure[],
    label: (eventId: string) => string,
  ) => {
    if (failures.length === 0) {
      return;
    }
    const lines = failures.map((f) => `${label(f.eventId)}: ${f.message}`);
    showToast(
      "warning",
      `Sales Reps were not copied to ${failures.length} duplicate${failures.length === 1 ? "" : "s"}; add reps there. ${lines.join("; ")}`,
      20000,
    );
  };

  const handleDuplicateToDates = async (
    dates: string[],
    autoQRForDates: boolean,
  ) => {
    if (!id) {
      return;
    }
    const valid = dates.every((d) => /^\d{4}-\d{2}-\d{2}$/.test(d));
    if (!valid || dates.length === 0) {
      showToast("error", "Add at least one valid date (YYYY-MM-DD)");
      return;
    }
    if (dates.length > 24) {
      showToast("error", "Maximum 24 dates per call");
      return;
    }
    // Sprint A Batch 4 A10.3: keep modal open if auto-QR is on so the
    // inline progress can render. Close immediately if it's off — fast
    // path returns to existing behavior.
    if (!autoQRForDates) {
      setShowDuplicateModal(false);
    }
    setIsDuplicating(true);
    setAutoQRProgress(null);
    try {
      const { ids: newIds, staffingCopyFailures } = await duplicateEventToDates(
        id,
        dates,
      );
      await queryClient.invalidateQueries({ queryKey: eventKeys.all });
      // The duplicates exist; any whose Sales Reps did not copy are named so the operator can add reps there.
      reportStaffingCopyFailures(
        staffingCopyFailures,
        (eventId) => dates[newIds.indexOf(eventId)] ?? eventId,
      );

      // Phase 1.1 Ship 3 Layer 3 — fire-and-forget generic CTA QR
      // generation for each new scheduled copy. Runs regardless of the
      // auto-QR checkbox below (which is about cloned per-rep CTAs).
      // If the source had subscription_signup_eligible=false,
      // duplicate inherits that and the trigger doesn't mint, so
      // regenerateGenericCTAQRs gracefully skips those events.
      // A class / table / seminar / speaking engagement has no generic CTA (2026-10-29): nothing to draw.
      // RM / TM (mig 1560): their browser publishes no QR (publish_event_cta_qr refuses them); the copies are saved
      // Incomplete and the events team publishes the QR, as for an event from their Create Event.
      if (user?.id && !eventTypeIsNoPromo(event?.type) && !fieldManager) {
        regenerateGenericCTAQRs(newIds, user.id).catch((err) => {
          console.warn("Generic CTA QR regen failed:", err);
        });
      }

      if (!autoQRForDates || !user?.id || fieldManager) {
        showToast(
          "success",
          fieldManager
            ? `Created ${newIds.length} scheduled event(s)${noPromo ? "" : "; the events team publishes their QR codes"}`
            : `Created ${newIds.length} scheduled event(s); QR generation skipped`,
        );
        setShowDuplicateModal(false); // internal review P3-4: the auto-QR-on, no-user path left it open
        navigate(`/events/${newIds[0]}`);
        return;
      }

      // Auto-QR path: keep modal open with progress UI.
      setAutoQRProgress({ current: 0, total: newIds.length });
      const result = await regenerateQRsForEvents(
        newIds,
        user.id,
        (cur, total) => setAutoQRProgress({ current: cur, total }),
      );

      if (result.failedTotal === 0) {
        showToast(
          "success",
          `Created ${newIds.length} scheduled event(s); generated ${result.regeneratedTotal} QR(s)`,
        );
      } else {
        // Partial failure — warn but still navigate. Operator can fix
        // per-event via the existing "Regenerate QRs" button.
        const affectedEvents = result.perEvent.filter(
          (p) => p.failed > 0 || p.error,
        ).length;
        showToast(
          "info",
          `Created ${newIds.length} scheduled event(s); ${result.regeneratedTotal} QRs generated, ${result.failedTotal} failed across ${affectedEvents} event(s). Use Regenerate QRs to retry.`,
        );
      }
      setShowDuplicateModal(false);
      navigate(`/events/${newIds[0]}`);
    } catch (error) {
      console.error("Error duplicating event to dates:", error);
      const msg =
        (error as { message?: string } | null)?.message ||
        "Failed to duplicate to dates";
      showToast("error", msg.replace(/^FIELD_MANAGER_[A-Z_]+:\s*/, ""));
    } finally {
      setIsDuplicating(false);
      setAutoQRProgress(null);
    }
  };

  // Regenerate all CTA QRs handler (Sprint A Batch 2 Ship D)
  const handleRegenerateAllQRs = async () => {
    if (!id || !event || !user?.id) {
      return;
    }
    if (
      !window.confirm(
        `Regenerate composite QR codes for all ${eventCTAs.length} CTA(s) on this event? Soft-deleted CTAs are skipped.`,
      )
    ) {
      return;
    }
    setRegenerating(true);
    setRegenResult(null);
    try {
      const result = await regenerateAllEventCTAQRs(id, event, user.id);
      setRegenResult({
        regenerated: result.regenerated,
        failed: result.failed,
        skipped: result.skipped,
      });
      if (result.failed === 0) {
        showToast(
          "success",
          `Regenerated ${result.regenerated} QR(s); skipped ${result.skipped}`,
        );
      } else {
        showToast(
          "info",
          `Regenerated ${result.regenerated}; ${result.failed} failed; ${result.skipped} skipped`,
        );
      }
      await queryClient.invalidateQueries({ queryKey: eventKeys.all });
    } catch (error) {
      console.error("Error regenerating QRs:", error);
      showToast(
        "error",
        error instanceof Error ? error.message : "Failed to regenerate QRs",
      );
    } finally {
      setRegenerating(false);
    }
  };

  const handleDuplicate = async (ctaIdsToCopy: string[]) => {
    if (!id) {
      return;
    }

    setShowDuplicateModal(false);
    setIsDuplicating(true);
    try {
      const { event: newEvent, staffingCopyFailures } = await duplicateEvent(
        id,
        ctaIdsToCopy.length > 0 ? ctaIdsToCopy : undefined,
      );
      showToast(
        "success",
        fieldManager && !noPromo
          ? "Event duplicated. The events team publishes its QR codes."
          : "Event duplicated successfully",
      );
      reportStaffingCopyFailures(staffingCopyFailures, () => "the new copy");
      await queryClient.invalidateQueries({ queryKey: eventKeys.all });
      navigate(`/events/${newEvent.id}`);
    } catch (error) {
      console.error("Error duplicating event:", error);
      // RM / TM: say why the server refused (out of their scope / a type they cannot create)
      const msg = (error as { message?: string } | null)?.message ?? "";
      showToast(
        "error",
        fieldManager && /FIELD_MANAGER_/.test(msg)
          ? msg.replace(/^FIELD_MANAGER_[A-Z_]+:\s*/, "")
          : "Failed to duplicate event",
      );
    } finally {
      setIsDuplicating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Back Button and Actions */}
      <div className="flex items-start justify-between gap-2">
        <button
          onClick={() => navigate(backTo)}
          className="text-gray-600 hover:text-gray-900 shrink-0"
        >
          <ArrowLeftIcon className="h-6 w-6" />
        </button>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {event &&
            canWalkIn &&
            ["scheduled", "in_progress"].includes(event.status || "") && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowWalkin(true)}
                title="Register a walk-in (and guests) and issue their reward"
              >
                + Walk-in
              </Button>
            )}
          {event &&
            canWalkIn &&
            canConfigure &&
            !event.not_advertised &&
            !noPromo && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => navigate(`/events/create?resume=${event.id}`)}
                title="Configure this event's reward, registration, audience, and invitation"
              >
                Configure Event
              </Button>
            )}
          {event && canStartAdvertising && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void startAdvertising()}
              disabled={startingAdvertising}
              data-testid="start-advertising"
              title="Turn this Gun Show - Not Advertised into a regular gun show (invitations and a landing page can then be set up)"
            >
              Start advertising
            </Button>
          )}
          {event && canConfigure && showsSetupIncomplete(event) && (
            <Button
              variant="secondary"
              size="sm"
              onClick={markSetupComplete}
              title="This event was saved from the create wizard to finish later. Mark it set up to remove the Incomplete badge."
            >
              Mark as set up
            </Button>
          )}
          {/* mig 1560 (owner 2026-11-03): Regional / Territory Managers duplicate events they can see again (the
              server checks visibility); every other setup action stays hidden for them. Not for the four event types
              they cannot create (REQ-2.3; the server refuses that copy too). */}
          {!(
            fieldManager &&
            FIELD_MANAGER_HIDDEN_EVENT_TYPES.has(event?.type ?? "")
          ) && (
            <Button
              variant="secondary"
              size="sm"
              icon={DocumentDuplicateIcon}
              onClick={openDuplicateModal}
              disabled={isDuplicating}
              isLoading={isDuplicating}
            >
              Duplicate
            </Button>
          )}
          {canConfigure && eventCTAs.length > 0 && (
            <Button
              variant="secondary"
              size="sm"
              onClick={handleRegenerateAllQRs}
              disabled={regenerating}
              isLoading={regenerating}
              title="Regenerate composite QR codes for every CTA on this event"
            >
              Regenerate QRs
            </Button>
          )}
        </div>
      </div>

      {showWalkin && event && (
        <EventWalkinPanel
          eventId={event.id}
          eventTitle={event.title}
          onClose={() => setShowWalkin(false)}
          onRegistered={() => {
            void refetchEvent();
            // A walk-in adds/updates an attendee — invalidate EVERY canonical view (attendees +
            // summary card + cohort tab + operations roster) so a new walk-in shows up immediately
            // everywhere, not just the raw attendee list (re-audit-4 HIGH). Keyed invalidation reaches
            // the AttendeesTab queries from here. Keys come from the shared source of truth the parity
            // test asserts against (re-audit-5).
            for (const key of attendanceInvalidationKeys(event.id)) {
              void queryClient.invalidateQueries({ queryKey: key });
            }
          }}
        />
      )}

      <EventInABoxWizard
        isOpen={showEibWizard && !noPromo}
        initialMode={eibInitialMode}
        onClose={() => setShowEibWizard(false)}
        onLaunched={() => {
          // Refresh the page data but DELIBERATELY leave the wizard open: the
          // operator must be able to read the per-step launch log (what was
          // created, what was skipped) before dismissing it.
          void refetchEvent();
        }}
        onAddCta={() => {
          // Post-launch hand-off: this modal has no built-in CTA step, so send the
          // operator straight to the event's Call to Action tab to add a CTA/QR.
          setShowEibWizard(false);
          setActiveTab("cta");
        }}
        event={
          event
            ? {
                id: event.id,
                title: event.title,
                start_date: event.start_date ?? null,
                type: event.type ?? null,
              }
            : null
        }
      />

      {regenResult && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-900">
          QR regeneration: {regenResult.regenerated} regenerated,{" "}
          {regenResult.failed} failed, {regenResult.skipped} skipped.{" "}
          <button
            onClick={() => setRegenResult(null)}
            className="text-blue-600 hover:text-blue-800 underline ml-2"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Centered Event Title and Info */}
      <div className="flex flex-col items-center justify-center text-center space-y-4">
        <div>
          {/* Editable Title */}
          {isEditingTitle ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                className="text-3xl font-bold text-gray-900 border border-gray-300 rounded-md px-3 py-1 focus:ring-primary-500 focus:border-primary-500"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    saveTitle();
                  }
                  if (e.key === "Escape") {
                    setIsEditingTitle(false);
                  }
                }}
              />
              <Button
                variant="primary"
                size="sm"
                onClick={saveTitle}
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Save"}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setIsEditingTitle(false)}
                disabled={isSaving}
              >
                Cancel
              </Button>
            </div>
          ) : (
            <h1
              className={`text-2xl sm:text-4xl font-bold text-gray-900 group inline-flex items-center gap-2 break-words ${
                canConfigure ? "cursor-pointer hover:text-primary-600" : ""
              }`}
              onClick={startEditingTitle}
              title={canConfigure ? "Click to edit title" : undefined}
            >
              {event.title}
              {canConfigure && (
                <PencilIcon className="h-5 w-5 opacity-0 group-hover:opacity-50 transition-opacity" />
              )}
            </h1>
          )}
          {/* Event ID */}
          <p className="text-sm text-gray-500 mt-1 font-mono">ID: {event.id}</p>
          <div className="flex items-center justify-center space-x-4 mt-3">
            {/* Status Dropdown — Smart Admin replacement R1 (2026-05-17):
                cancelled events are permanent per Heather's requirement.
                Frontend matches the DB BEFORE UPDATE trigger that blocks
                status change away from 'cancelled'. */}
            {/* Derived liveness badge. The <select> beside it shows the SAVED
                status, which nothing automates into 'in_progress' — so it is not
                evidence the event is happening now. This badge is derived from
                the event's own timestamps and appears only when it says
                something the saved status does not. */}
            <EventStatusBadge event={event} onlyWhenDerived />
            <select
              value={event.status || "scheduled"}
              onChange={(e) =>
                handleStatusChange(e.target.value as EventStatus)
              }
              disabled={
                isUpdatingStatus ||
                event.status === "cancelled" ||
                !canConfigure
              }
              title={
                event.status === "cancelled"
                  ? "Cancelled events are permanent and cannot be re-opened. Create a new event if needed."
                  : undefined
              }
              className={`px-3 py-1 rounded-full text-sm font-medium border-0 appearance-none pr-8 bg-no-repeat bg-right ${
                event.status === "cancelled"
                  ? "cursor-not-allowed"
                  : "cursor-pointer"
              } ${
                event.status === "completed"
                  ? "bg-green-100 text-green-800"
                  : event.status === "cancelled"
                    ? "bg-red-100 text-red-800"
                    : event.status === "scheduled"
                      ? "bg-blue-100 text-blue-800"
                      : event.status === "in_progress"
                        ? "bg-yellow-100 text-yellow-800"
                        : event.status === "postponed"
                          ? "bg-orange-100 text-orange-800"
                          : "bg-gray-100 text-gray-800"
              } ${isUpdatingStatus ? "opacity-50" : ""}`}
              style={{
                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                backgroundPosition: "right 0.25rem center",
                backgroundSize: "1.25em 1.25em",
              }}
            >
              <option value="scheduled">Scheduled</option>
              <option value="in_progress">In Progress</option>
              {/* Completed: admin/superadmin only (2026-09-30), DB-enforced. Non-admins still see an already
                  completed event's status (disabled) but are never offered it. */}
              {completedOptionState(user?.role, event.status) !== "hidden" && (
                <option
                  value="completed"
                  disabled={
                    completedOptionState(user?.role, event.status) ===
                    "shown_disabled"
                  }
                >
                  Completed
                </option>
              )}
              <option value="cancelled">Cancelled</option>
              <option value="postponed">Postponed</option>
            </select>
            {/* Editable Type — uses shared VISIBLE_EVENT_TYPES so the
                dropdown stays in sync with create/edit. R1 audit must-fix:
                this previously had its own divergent 5-value list. If
                the event's current type is deprecated, surface it in
                the dropdown so the operator sees what's set. */}
            {isEditingType ? (
              <div className="flex items-center gap-2">
                <select
                  value={editType}
                  onChange={(e) => setEditType(e.target.value)}
                  className="px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800 border border-purple-300"
                  autoFocus
                >
                  <option value="">Select type...</option>
                  {(VISIBLE_EVENT_TYPES.some((t) => t.value === editType)
                    ? VISIBLE_EVENT_TYPES
                    : editType
                      ? [
                          ...VISIBLE_EVENT_TYPES,
                          ...EVENT_TYPES.filter((t) => t.value === editType),
                        ]
                      : VISIBLE_EVENT_TYPES
                  ).map((et) => (
                    <option key={et.value} value={et.value}>
                      {et.label}
                    </option>
                  ))}
                </select>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={saveType}
                  disabled={isSaving}
                >
                  Save
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setIsEditingType(false)}
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800 capitalize ${
                  canConfigure
                    ? "cursor-pointer hover:bg-purple-200 transition-colors"
                    : ""
                }`}
                onClick={startEditingType}
                title={canConfigure ? "Click to change type" : undefined}
              >
                {event.type || "N/A"}
              </span>
            )}
            {event.not_advertised && (
              <span
                className="px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700"
                title="Gun Show - Not Advertised: no Event in a Box invitations and no landing page; still listed publicly"
              >
                Not advertised
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Cancellation Info Banner */}
      {event.status === "cancelled" && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <XCircleIcon className="h-5 w-5 text-red-500 mt-0.5 shrink-0" />
            <div>
              {event.cancellation_reason && (
                <p className="text-sm font-medium text-red-800">
                  Cancelled:{" "}
                  {CANCELLATION_REASONS.find(
                    (r) => r.value === event.cancellation_reason,
                  )?.label || event.cancellation_reason}
                </p>
              )}
              {event.cancellation_notes && (
                <p className="text-sm text-red-700 mt-1">
                  {event.cancellation_notes}
                </p>
              )}
              <p className="text-xs text-red-700 mt-2">
                Event fields are locked. Cancellation is permanent — create a
                new event if you need to reschedule. Comments below remain
                editable for documentation.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Quick Info Bar */}
      <div className="bg-white rounded-lg shadow p-6">
        {isEditingDateTime ? (
          /* Date/Time Edit Form */
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-gray-900">
                Edit Date & Time
              </h3>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setIsEditingDateTime(false)}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={saveDateTime}
                  disabled={isSaving}
                >
                  {isSaving ? "Saving..." : "Save"}
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <DatePicker
                  selected={
                    editDateTime.startDate
                      ? new Date(editDateTime.startDate + "T00:00:00")
                      : null
                  }
                  onChange={(date: Date | null) =>
                    setEditDateTime((prev) => {
                      const startDate = date
                        ? date.toISOString().split("T")[0]
                        : "";
                      // Auto-advance a now-stale end date so it never sits before the start
                      // (YYYY-MM-DD strings sort chronologically). Avoids the "end before start"
                      // rejection when the operator moves the start forward.
                      const endStale =
                        !!startDate &&
                        !!prev.endDate &&
                        prev.endDate < startDate;
                      return {
                        ...prev,
                        startDate,
                        endDate: endStale ? startDate : prev.endDate,
                      };
                    })
                  }
                  dateFormat="MMM d, yyyy"
                  placeholderText="Select start date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  todayButton="Today"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Time
                </label>
                <input
                  type="time"
                  value={editDateTime.startTime}
                  onChange={(e) =>
                    setEditDateTime((prev) => ({
                      ...prev,
                      startTime: e.target.value,
                    }))
                  }
                  disabled={editDateTime.allDay}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <DatePicker
                  selected={
                    editDateTime.endDate
                      ? new Date(editDateTime.endDate + "T00:00:00")
                      : null
                  }
                  onChange={(date: Date | null) =>
                    setEditDateTime((prev) => ({
                      ...prev,
                      endDate: date ? date.toISOString().split("T")[0] : "",
                    }))
                  }
                  minDate={
                    editDateTime.startDate
                      ? new Date(editDateTime.startDate + "T00:00:00")
                      : undefined
                  }
                  dateFormat="MMM d, yyyy"
                  placeholderText="Select end date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  todayButton="Today"
                  isClearable
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Time
                </label>
                <input
                  type="time"
                  value={editDateTime.endTime}
                  onChange={(e) =>
                    setEditDateTime((prev) => ({
                      ...prev,
                      endTime: e.target.value,
                    }))
                  }
                  disabled={editDateTime.allDay}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100"
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="allDay"
                  checked={editDateTime.allDay}
                  onChange={(e) =>
                    setEditDateTime((prev) => ({
                      ...prev,
                      allDay: e.target.checked,
                    }))
                  }
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label
                  htmlFor="allDay"
                  className="text-sm font-medium text-gray-700"
                >
                  All Day Event
                </label>
              </div>
              <div className="w-64">
                <TimezoneSelector
                  value={editTimezone}
                  onChange={setEditTimezone}
                  required
                  label="Time Zone"
                />
              </div>
            </div>
            <EventDailyHoursEditor
              app={editDailyHoursApp}
              value={editDailyHours}
              onChange={setEditDailyHours}
              saved={eventDayHoursRows}
              readOnlyReason={editDailyHoursReadOnly}
              loadError={dayHoursLoadError}
            />
          </div>
        ) : isEditingLocation ? (
          /* Location Edit Form */
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-gray-900">
                Edit Location
              </h3>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setIsEditingLocation(false)}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={saveLocation}
                  disabled={isSaving}
                >
                  {isSaving ? "Saving..." : "Save"}
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location Type
                </label>
                <select
                  value={editLocation.locationType}
                  onChange={(e) =>
                    setEditLocation((prev) => ({
                      ...prev,
                      locationType: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="physical">Physical</option>
                  <option value="virtual">Virtual</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location Name
                </label>
                <input
                  type="text"
                  value={editLocation.locationName}
                  onChange={(e) =>
                    setEditLocation((prev) => ({
                      ...prev,
                      locationName: e.target.value,
                    }))
                  }
                  placeholder="e.g., Conference Center"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
            {(editLocation.locationType === "physical" ||
              editLocation.locationType === "hybrid") && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address
                  </label>
                  <input
                    type="text"
                    value={editLocation.addressLine1}
                    onChange={(e) =>
                      setEditLocation((prev) => ({
                        ...prev,
                        addressLine1: e.target.value,
                      }))
                    }
                    placeholder="Street address"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    value={editLocation.city}
                    onChange={(e) =>
                      setEditLocation((prev) => ({
                        ...prev,
                        city: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      State
                    </label>
                    <input
                      type="text"
                      value={editLocation.state}
                      onChange={(e) =>
                        setEditLocation((prev) => ({
                          ...prev,
                          state: e.target.value.toUpperCase(),
                        }))
                      }
                      maxLength={2}
                      placeholder="TX"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 uppercase"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Zip
                    </label>
                    <input
                      type="text"
                      value={editLocation.postalCode}
                      onChange={(e) =>
                        setEditLocation((prev) => ({
                          ...prev,
                          postalCode: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>
              </div>
            )}
            {(editLocation.locationType === "virtual" ||
              editLocation.locationType === "hybrid") && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Meeting URL
                </label>
                <input
                  type="url"
                  value={editLocation.meetingUrl}
                  onChange={(e) =>
                    setEditLocation((prev) => ({
                      ...prev,
                      meetingUrl: e.target.value,
                    }))
                  }
                  placeholder="https://zoom.us/..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            )}
            {editLocation.locationType === "virtual" && (
              <div className="w-48">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State (required)
                </label>
                <input
                  type="text"
                  value={editLocation.state}
                  onChange={(e) =>
                    setEditLocation((prev) => ({
                      ...prev,
                      state: e.target.value.toUpperCase(),
                    }))
                  }
                  maxLength={2}
                  placeholder="TX"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 uppercase"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Required for insurance/legal purposes
                </p>
              </div>
            )}
          </div>
        ) : (
          /* Read-only Quick Info Bar */
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Date */}
            <div
              className={`flex items-start space-x-3 group p-2 -m-2 rounded-lg ${
                canConfigure
                  ? "cursor-pointer hover:bg-gray-50 transition-colors"
                  : ""
              }`}
              onClick={startEditingDateTime}
              title={canConfigure ? "Click to edit date/time" : undefined}
            >
              <CalendarIcon className="h-6 w-6 text-gray-400 mt-1 group-hover:text-primary-500" />
              <div className="flex-1">
                <p className="text-sm text-gray-500">Date</p>
                <p className="text-base font-medium text-gray-900 group-hover:text-primary-600">
                  {event.date_tbd ? (
                    <span className="text-yellow-600 bg-yellow-50 px-2 py-0.5 rounded">
                      TBD
                    </span>
                  ) : (
                    formatEventDateRange(
                      event.start_time,
                      event.end_time,
                      event.timezone,
                    )
                  )}
                </p>
              </div>
              {canConfigure && (
                <PencilIcon className="h-4 w-4 text-gray-300 opacity-0 group-hover:opacity-100 mt-1" />
              )}
            </div>
            {/* Time */}
            <div
              className={`flex items-start space-x-3 group p-2 -m-2 rounded-lg ${
                canConfigure
                  ? "cursor-pointer hover:bg-gray-50 transition-colors"
                  : ""
              }`}
              onClick={startEditingDateTime}
              title={canConfigure ? "Click to edit date/time" : undefined}
            >
              <ClockIcon className="h-6 w-6 text-gray-400 mt-1 group-hover:text-primary-500" />
              <div className="flex-1">
                <p className="text-sm text-gray-500">Time</p>
                <p className="text-base font-medium text-gray-900 group-hover:text-primary-600">
                  {event.date_tbd ? (
                    <span className="text-yellow-600 bg-yellow-50 px-2 py-0.5 rounded">
                      TBD
                    </span>
                  ) : event.all_day ? (
                    "All Day"
                  ) : eventDailyHours ? (
                    <>
                      Varies by day
                      {event.timezone && (
                        <span className="ml-1 text-sm text-gray-500 font-normal">
                          {US_TIMEZONES.find(
                            (tz) => tz.value === event.timezone,
                          )?.abbr || event.timezone}
                        </span>
                      )}
                    </>
                  ) : (
                    <>
                      {`${formatEventTime(event.start_time, event.timezone)}${event.end_time ? ` - ${formatEventTime(event.end_time, event.timezone)}` : ""}`}
                      {event.timezone && (
                        <span className="ml-1 text-sm text-gray-500 font-normal">
                          {US_TIMEZONES.find(
                            (tz) => tz.value === event.timezone,
                          )?.abbr || event.timezone}
                        </span>
                      )}
                    </>
                  )}
                </p>
                {!event.date_tbd && !event.all_day && eventDailyHours && (
                  <EventDailyHoursList
                    days={eventDailyHours}
                    className="mt-1 space-y-0.5"
                  />
                )}
                {!event.date_tbd &&
                  !event.all_day &&
                  !eventDailyHours &&
                  dayHoursQuery.isError && (
                    <p
                      className="mt-2 text-xs text-red-700"
                      data-testid="event-daily-hours-load-error"
                    >
                      Could not load the hours for each day.
                    </p>
                  )}
                {!event.date_tbd &&
                  !event.all_day &&
                  !eventDailyHours &&
                  eventDayHoursRows !== undefined && (
                    <EventDailyHoursNotice
                      event={event}
                      rows={eventDayHoursRows}
                      editHint="To set them, click the date or time and use “Set hours for each day”."
                    />
                  )}
              </div>
              {canConfigure && (
                <PencilIcon className="h-4 w-4 text-gray-300 opacity-0 group-hover:opacity-100 mt-1" />
              )}
            </div>
            {/* Location */}
            <div
              className={`flex items-start space-x-3 group p-2 -m-2 rounded-lg ${
                canConfigure
                  ? "cursor-pointer hover:bg-gray-50 transition-colors"
                  : ""
              }`}
              onClick={startEditingLocation}
              title={canConfigure ? "Click to edit location" : undefined}
            >
              <MapPinIcon className="h-6 w-6 text-gray-400 mt-1 group-hover:text-primary-500" />
              <div className="flex-1">
                <p className="text-sm text-gray-500">
                  Location{" "}
                  {canConfigure && (
                    <span className="text-xs text-gray-400">
                      (click to edit)
                    </span>
                  )}
                </p>
                {event.location_type === "virtual" ? (
                  <div>
                    <p className="text-base font-medium text-gray-900 group-hover:text-primary-600">
                      Virtual Meeting
                    </p>
                    {event.meeting_url && (
                      <p className="text-sm text-gray-500 truncate">
                        {event.meeting_url}
                      </p>
                    )}
                  </div>
                ) : (
                  <div>
                    <p className="text-base font-medium text-gray-900 group-hover:text-primary-600">
                      {event.location_name || "No location name"}
                    </p>
                    {event.location_address && (
                      <p className="text-sm text-gray-500">
                        {[
                          event.location_address.address_line1,
                          event.location_address.city,
                          event.location_address.state,
                          event.location_address.postal_code,
                        ]
                          .filter(Boolean)
                          .join(", ") || "No address entered"}
                      </p>
                    )}
                    {!event.location_address && (
                      <p className="text-sm text-gray-400 italic">
                        No address entered - click to add
                      </p>
                    )}
                  </div>
                )}
              </div>
              {canConfigure && (
                <PencilIcon className="h-4 w-4 text-gray-300 opacity-0 group-hover:opacity-100 mt-1" />
              )}
            </div>
          </div>
        )}
      </div>

      {/* MSR QA 10-20: a rep on the list has time off on one of this event's days (warning only) */}
      <TimeOffConflictBanner eventId={event.id} />

      {/* Event Details Section — ownership, sales rep chain, class sub-classifications, voucher used */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
            Event Details
          </h3>
          {canConfigure && !isCancelled && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowHierarchyEditor(true)}
              data-testid="edit-sales-hierarchy-button"
            >
              Edit sales hierarchy
            </Button>
          )}
        </div>
        {/* Row 1: Ownership + Sales Rep Chain */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {event.event_ownership && (
            <div>
              <p className="text-xs text-gray-500">Event Ownership</p>
              <p className="text-sm font-medium text-gray-900">
                {EVENT_OWNERSHIP_OPTIONS.find(
                  (o) => o.value === event.event_ownership,
                )?.label || event.event_ownership}
              </p>
            </div>
          )}
          {/* Sales Rep. REQ-8.3 (RM/TM role setup 2026-10-29): no inline dropdown any more (it moved only the Lead, and
              its Save / Cancel sat under the next column); "Edit sales hierarchy" above opens the create wizard's box. */}
          <div>
            <p className="text-xs text-gray-500 mb-1">Sales Rep</p>
            <p className="text-sm font-medium text-gray-900">
              {event.is_self_service ? (
                <span className="text-gray-700">No Rep — Self-Service</span>
              ) : (
                salesHierarchy?.repName || (
                  <span className="text-gray-400 italic">Unassigned</span>
                )
              )}
            </p>
          </div>
          {salesHierarchy?.tm && (
            <div>
              <p className="text-xs text-gray-500">Territory Manager</p>
              <p className="text-sm font-medium text-gray-900">
                {salesHierarchy.tm}
              </p>
            </div>
          )}
          {salesHierarchy?.rm && (
            <div>
              <p className="text-xs text-gray-500">Regional Manager</p>
              <p className="text-sm font-medium text-gray-900">
                {salesHierarchy.rm}
              </p>
            </div>
          )}
          {salesHierarchy?.sd && (
            <div>
              <p className="text-xs text-gray-500">Sales Director</p>
              <p className="text-sm font-medium text-gray-900">
                {salesHierarchy.sd}
              </p>
            </div>
          )}
        </div>

        {/* Row 2: Class Sub-Classifications (only when type = class) */}
        {event.type === "class" &&
          (event.class_delivery_method ||
            event.usls_curriculum ||
            event.class_subtype) && (
            <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-100">
              {event.class_delivery_method && (
                <div>
                  <p className="text-xs text-gray-500">Delivery Method</p>
                  <p className="text-sm font-medium text-gray-900">
                    {CLASS_DELIVERY_METHODS.find(
                      (m) => m.value === event.class_delivery_method,
                    )?.label || event.class_delivery_method}
                  </p>
                </div>
              )}
              {event.usls_curriculum && (
                <div>
                  <p className="text-xs text-gray-500">Curriculum</p>
                  <p className="text-sm font-medium text-gray-900">
                    {USLS_CURRICULUM_OPTIONS.find(
                      (c) => c.value === event.usls_curriculum,
                    )?.label || event.usls_curriculum}
                  </p>
                </div>
              )}
              {event.class_subtype && (
                <div>
                  <p className="text-xs text-gray-500">Class Subtype</p>
                  <p className="text-sm font-medium text-gray-900">
                    {CLASS_SUBTYPES.find((s) => s.value === event.class_subtype)
                      ?.label || event.class_subtype}
                  </p>
                </div>
              )}
              <div>
                <label
                  className={`flex items-center gap-2 ${
                    isCancelled
                      ? "cursor-not-allowed opacity-60"
                      : "cursor-pointer"
                  }`}
                >
                  <input
                    type="checkbox"
                    disabled={isCancelled || !canConfigure}
                    checked={!!event.usls_curriculum_used}
                    onChange={(e) =>
                      saveVoucherFlags("usls_curriculum_used", e.target.checked)
                    }
                    className="h-4 w-4 text-primary-600 border-gray-300 rounded disabled:opacity-50"
                  />
                  <span className="text-sm text-gray-700">
                    USLS Curriculum Used
                  </span>
                </label>
              </div>
            </div>
          )}

        {/* Voucher Used toggle (for table, gun_show, conference, speaking_engagement) */}
        {VOUCHER_USED_EVENT_TYPES.includes(event.type || "") && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <label
              className={`flex items-center gap-2 ${
                isCancelled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
              }`}
            >
              <input
                type="checkbox"
                disabled={isCancelled || !canConfigure}
                checked={!!event.voucher_used}
                onChange={(e) =>
                  saveVoucherFlags("voucher_used", e.target.checked)
                }
                className="h-4 w-4 text-primary-600 border-gray-300 rounded disabled:opacity-50"
              />
              <span className="text-sm text-gray-700">Voucher Used</span>
            </label>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav
            className="flex space-x-4 px-4 sm:px-6 overflow-x-auto"
            aria-label="Tabs"
          >
            {tabs.map(({ id, label, icon: Icon, badge, badgeColor }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`
                  flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap shrink-0
                  ${
                    activeTab === id
                      ? "border-primary-500 text-primary-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }
                `}
              >
                <Icon className="h-5 w-5" />
                <span>{label}</span>
                {badge > 0 && (
                  <span
                    className={`inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium text-white rounded-full min-w-[20px] ${badgeColor}`}
                  >
                    {badge > 99 ? "99+" : badge}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-4 sm:p-6">
          {fieldManager &&
            FIELD_MANAGER_READ_ONLY_EVENT_TABS.has(activeTab) && (
              <p
                className="mb-4 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600"
                data-testid="field-manager-view-only"
              >
                View only: Regional and Territory Managers can see how this
                event is set up but do not change it.
              </p>
            )}
          <fieldset
            className="min-w-0"
            disabled={
              fieldManager && FIELD_MANAGER_READ_ONLY_EVENT_TABS.has(activeTab)
            }
          >
            {activeTab === "overview" && <EventDaysStatusPanel eventId={id!} />}
            {activeTab === "overview" && (
              <OverviewTab
                event={event}
                eventId={id!}
                googleMapsLink={googleMapsLink}
                readOnly={!canConfigure}
              />
            )}
            {activeTab === "operations" && canWalkIn && fieldManager && (
              <VenueCheckinToggle
                eventId={event.id}
                disabled={event.self_checkin_disabled === true}
              />
            )}
            {activeTab === "operations" && canWalkIn && (
              <EventOperationsTab
                eventId={event.id}
                onWalkIn={() => setShowWalkin(true)}
                canCheckIn={["scheduled", "in_progress"].includes(
                  event.status || "",
                )}
                endTime={event.end_time}
              />
            )}
            {activeTab === "attendees" && (
              <>
                <AttendeesTab eventId={id!} event={event} />
                {/* Phase 2 — manager/admin/superadmin only retroactive
                  attribution UI. Server-side RPC also enforces the
                  role, so hiding here is a UX-only check. */}
                {(() => {
                  const role = user?.role?.toLowerCase().replace(/_/g, "");
                  if (
                    role === "manager" ||
                    role === "regionalmanager" ||
                    role === "territorymanager" ||
                    role === "admin" ||
                    role === "superadmin"
                  ) {
                    return <AttributionOverridesSection eventId={id!} />;
                  }
                  return null;
                })()}
              </>
            )}
            {activeTab === "tickets" && featureFlags.eventTicketing && (
              <TicketsTab eventId={id!} eventTitle={event.title} />
            )}
            {activeTab === "cta" &&
              (noPromo ? (
                <EventHostCTAsPanel event={event} />
              ) : (
                <CallToActionTab event={event} />
              ))}
            {activeTab === "landing-page" && !noPromo && (
              <LandingPageTab event={event} />
            )}
            {activeTab === "rewards" && <RewardsTab event={event} />}
            {activeTab === "drawing" && <DrawingTab eventId={id!} />}
            {activeTab === "verification" && (
              <MemberVerificationTab eventId={id!} />
            )}
            {activeTab === "vouchers" && (
              <VouchersTab
                eventId={id!}
                onGoToSettings={() => setActiveTab("cta")}
              />
            )}
            {activeTab === "campaigns" && !noPromo && (
              <EventCampaignsTab eventId={id!} />
            )}
            {activeTab === "tasks" && <TasksTab eventId={id!} />}
            {activeTab === "lists" && !noPromo && <ListsTab eventId={id!} />}
            {activeTab === "documents" && <DocumentsTab eventId={id!} />}
            {activeTab === "finance" && showFinanceTab && financeAccess && (
              <BudgetExpensesTab eventId={id!} access={financeAccess} />
            )}
            {activeTab === "sales-reps" && (
              <SalesRepsTab eventId={id!} eventTitle={event.title} />
            )}
            {activeTab === "sharing" && <SharingTab eventId={id!} />}
            {activeTab === "reports" && (
              <ReportsTab eventId={id!} eventTimezone={event.timezone} />
            )}
          </fieldset>
        </div>
      </div>

      {/* Cancellation Reason Modal */}
      <CancellationReasonModal
        isOpen={showCancellationModal}
        onClose={() => setShowCancellationModal(false)}
        onConfirm={handleCancellationConfirm}
        isLoading={isUpdatingStatus}
        eventTitle={event?.title}
      />

      {showHierarchyEditor && event && (
        <EditSalesHierarchyDialog
          event={event}
          onClose={() => setShowHierarchyEditor(false)}
        />
      )}

      {showDuplicateModal && (
        <DuplicateEventDialog
          isOpen
          eventTitle={event?.title}
          ctas={eventCTAs.map((cta: any) => ({
            id: cta.id,
            name: cta.name,
            cta_type: cta.cta_type,
          }))}
          isWorking={isDuplicating}
          qrProgress={autoQRProgress}
          canPublishQr={!fieldManager}
          onClose={() => setShowDuplicateModal(false)}
          onDuplicateOne={(ctaIds) => handleDuplicate(ctaIds)}
          onDuplicateToDates={(dates, autoQR) =>
            handleDuplicateToDates(dates, autoQR)
          }
        />
      )}
    </div>
  );
};

// ========================================
// TAB COMPONENTS
// ========================================

// OverviewTab has been extracted to src/components/events/tabs/OverviewTab.tsx

export const AttendeesTab: React.FC<{ eventId: string; event: Event }> = ({
  eventId,
  event,
}) => {
  const [attendeeView, setAttendeeView] = useState<
    "list" | "checkin-feed" | "attendance-report"
  >("list");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [checkingIn, setCheckingIn] = useState<string | null>(null);
  const [cancelingId, setCancelingId] = useState<string | null>(null);
  const [checkInModalAttendee, setCheckInModalAttendee] =
    useState<EventAttendee | null>(null);
  const [showListModal, setShowListModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [qrModalAttendee, setQrModalAttendee] = useState<{
    name: string;
    email?: string;
    checkinCode: string;
  } | null>(null);
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  // Fetch attendees
  const {
    data: attendees,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["event-attendees", eventId],
    queryFn: () => getEventAttendees(eventId),
  });

  // Canonical attendance summary (get_event_attendance_summary) — the SAME resolver the summary
  // export and CSV use. Its cohort counts (registered = advance registrants across checked-in/
  // awaiting/no-show/incomplete; walk_in as its own valid cohort) are authoritative, so the dashboard
  // agrees with the report instead of reconstructing counts from raw status (re-audit HIGH).
  const { data: attendanceSummary, isError: summaryError } = useQuery({
    queryKey: ["event-attendance-summary", eventId],
    queryFn: () => getAttendanceSummary(eventId),
    enabled: !!eventId,
  });

  // Canonical per-attendee cohort (get_event_attendance_roster) so the Registered/Walk-ins TABS
  // filter by the SAME cohort the cards count — not a raw-status reconstruction that dropped
  // confirmed / no-show / evidence-incomplete rows (re-audit-3 HIGH). Loads all pages.
  const { data: cohortById, isError: cohortsError } = useQuery({
    queryKey: ["event-attendance-cohorts", eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const map = new Map<string, AttendanceCohort>();
      let cursor: { sort: string; id: string } | null = null;
      do {
        const page = await getAttendanceRosterPage(eventId, "all", cursor, 200);
        for (const r of page.rows) {
          map.set(r.attendee_id, r.cohort);
        }
        cursor = page.nextCursor;
      } while (cursor);
      return map;
    },
  });

  // One place every attendee mutation refreshes ALL canonical attendance views so the cards and tabs
  // stay in sync — not just the raw attendee list (re-audit-4 HIGH). Route walk-in registration,
  // check-in, count confirmation, CSV import, etc. through this instead of a bare attendee refetch.
  const refreshAttendance = React.useCallback(async () => {
    await Promise.all([
      refetch(),
      queryClient.invalidateQueries({
        queryKey: ["event-attendance-summary", eventId],
      }),
      queryClient.invalidateQueries({
        queryKey: ["event-attendance-cohorts", eventId],
      }),
      queryClient.invalidateQueries({
        queryKey: ["event-operations-roster", eventId],
      }),
    ]);
  }, [refetch, queryClient, eventId]);

  // Fetch available lists
  const { data: availableLists } = useQuery({
    // Distinct key per data shape (2026-10-16): the same key held different rows/columns on other
    // screens, and with the 5-minute staleTime whichever screen fetched first won.
    queryKey: ["lists", "id-name-description"],
    queryFn: async () => {
      // Paged: PROD has more segments than PostgREST's 1000-row cap.
      return fetchAllRows<{
        id: string;
        name: string;
        description: string | null;
      }>((from, to) =>
        pageByNameThenId(
          supabase.from("lists").select("id, name, description"),
          from,
          to,
        ),
      );
    },
  });

  // Fetch linked lists for this event
  const { data: linkedLists, refetch: refetchLinkedLists } = useQuery({
    queryKey: ["event-lists", eventId],
    queryFn: () => getEventListAssociations(eventId),
  });

  // Handle linking a list
  const handleLinkList = async (listId: string) => {
    try {
      const existingIds =
        (linkedLists as EventListLink[] | undefined)?.map((l) => l.list_id) ||
        [];
      if (existingIds.includes(listId)) {
        showToast("warning", "List is already linked to this event");
        return;
      }

      const { error } = await supabase.from("event_lists").insert({
        event_id: eventId,
        list_id: listId,
      });

      if (error) {
        throw error;
      }

      await refetchLinkedLists();
      showToast("success", "List linked successfully");
      setShowListModal(false);
    } catch (error) {
      console.error("Error linking list:", error);
      showToast("error", "Failed to link list");
    }
  };

  // Handle unlinking a list
  const handleUnlinkList = async (listId: string) => {
    try {
      const { error } = await supabase
        .from("event_lists")
        .delete()
        .eq("event_id", eventId)
        .eq("list_id", listId);

      if (error) {
        throw error;
      }

      await refetchLinkedLists();
      showToast("success", "List unlinked successfully");
    } catch (error) {
      console.error("Error unlinking list:", error);
      showToast("error", "Failed to unlink list");
    }
  };

  // Handle CSV file upload
  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      const text = await file.text();
      const lines = text.split("\n").filter((line) => line.trim());
      const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());

      // Find email and name columns
      const emailIndex = headers.findIndex((h) => h.includes("email"));
      const firstNameIndex = headers.findIndex(
        (h) => h.includes("first") && h.includes("name"),
      );
      const lastNameIndex = headers.findIndex(
        (h) => h.includes("last") && h.includes("name"),
      );
      const nameIndex = headers.findIndex(
        (h) => h === "name" || h === "full_name",
      );

      if (emailIndex === -1) {
        showToast("error", "CSV must have an email column");
        return;
      }

      // Parse data rows
      const attendeesToAdd = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i]
          .split(",")
          .map((v) => v.trim().replace(/^"|"$/g, ""));
        const email = values[emailIndex];
        if (!email || !email.includes("@")) {
          continue;
        }

        let firstName = "";
        let lastName = "";

        if (firstNameIndex !== -1) {
          firstName = values[firstNameIndex] || "";
        }
        if (lastNameIndex !== -1) {
          lastName = values[lastNameIndex] || "";
        }
        if (nameIndex !== -1 && !firstName && !lastName) {
          const nameParts = (values[nameIndex] || "").split(" ");
          firstName = nameParts[0] || "";
          lastName = nameParts.slice(1).join(" ") || "";
        }

        attendeesToAdd.push({ email, firstName, lastName });
      }

      if (attendeesToAdd.length === 0) {
        showToast("error", "No valid attendees found in CSV");
        return;
      }

      // Add attendees (create contacts if needed and add to event)
      let addedCount = 0;
      let skippedCount = 0;
      let failedCount = 0;
      let firstFailure: string | null = null;
      for (const attendee of attendeesToAdd) {
        // Check if contact exists
        const { data: existingContact } = await supabase
          .from("contacts")
          .select("id")
          .eq("email", attendee.email)
          // .single() errors when two contacts share the email, which read as "no contact" and
          // created a third. Take one.
          .limit(1)
          .maybeSingle();

        let contactId = existingContact?.id;
        let createdContact = false;

        // Create contact if doesn't exist
        if (!contactId) {
          const { data: newContact, error: createError } = await supabase
            .from("contacts")
            .insert({
              email: attendee.email,
              // contacts.first_name is NOT NULL (QA and PROD): a CSV row with no name column
              // used to fail here and be silently dropped.
              first_name: attendee.firstName || "",
              last_name: attendee.lastName || undefined,
              source: "event_import",
            })
            .select("id")
            .single();

          if (createError || !newContact?.id) {
            console.error("Error creating contact:", createError);
            failedCount++;
            continue;
          }
          contactId = newContact.id;
          createdContact = true;
        }

        // Add as event attendee
        const { data: existingAttendee } = await supabase
          .from("event_attendees")
          .select("id")
          .eq("event_id", eventId)
          .eq("contact_id", contactId)
          .maybeSingle();

        // There is NO unique(event_id, contact_id) constraint (the real one is the 4-col
        // no_duplicate_attendees), so the previous upsert onConflict:"event_id,contact_id"
        // matched no index and failed silently. Find-then-insert instead; skip if already
        // on the event.
        if (existingAttendee?.id) {
          // Already on the event — skipped, NOT imported (audit F10: don't inflate the count).
          skippedCount++;
        } else {
          // Fixed 2026-10-03: this wrote attendance_status 'proposed' (refused by the CHECK) and no
          // attendee_type (NOT NULL, no default), so every row failed and the toast still said
          // "Imported 0". See OPERATOR_ADDED_ATTENDANCE_STATUS.
          const row = createdContact
            ? {
                event_id: eventId,
                contact_id: contactId,
                attendee_type: "prospect" as const,
                attendance_status: OPERATOR_ADDED_ATTENDANCE_STATUS,
              }
            : await buildOperatorAddedAttendeeRow(eventId!, contactId);
          const { error: attendeeError } = await supabase
            .from("event_attendees")
            .insert(row);
          if (attendeeError) {
            console.error("Error adding attendee:", attendeeError);
            failedCount++;
            if (!firstFailure) {
              firstFailure = attendeeError.message;
            }
          } else {
            addedCount++;
          }
        }
      }

      await refreshAttendance();
      const skippedNote =
        skippedCount > 0
          ? ` (${skippedCount} already on the event, skipped)`
          : "";
      if (failedCount > 0) {
        showToast(
          addedCount > 0 ? "warning" : "error",
          `Imported ${addedCount} attendees${skippedNote}; ${failedCount} could not be added${firstFailure ? `: ${firstFailure}` : ""}`,
        );
      } else {
        showToast("success", `Imported ${addedCount} attendees${skippedNote}`);
      }
      setShowUploadModal(false);
    } catch (error) {
      console.error("Error processing CSV:", error);
      showToast("error", "Failed to process CSV file");
    }
  };

  // Capacity metrics — CANONICAL ONLY. Every number here comes from
  // get_event_attendance_summary via canonicalAttendance(); nothing on this page counts
  // raw event_attendees rows any more.
  //
  // Fixed 2026-09-09: `attended` used to be `rows where attendance_status='attended'`.
  // On PROD event 51981c06 that returned 23 while the Overview funnel printed the
  // resolver's attended_headcount (16) under the same word. A row is not a person —
  // a party of four is one row and a zero-count companion row is still one body — so
  // the raw count only agreed with the resolver by coincidence. "Attended" is now
  // advance check-ins PLUS walk-ins, with walk-ins kept visible as their own card.
  //
  // `null` while the summary is loading or errored, so the card shows a loading/error
  // state rather than a known-inaccurate number (re-audit-4 MEDIUM).
  const canonical = React.useMemo(
    () => (attendanceSummary ? canonicalAttendance(attendanceSummary) : null),
    [attendanceSummary],
  );

  const metrics = React.useMemo(
    () => ({
      registered: canonical
        ? canonical.registeredRows
        : (null as number | null),
      walkIns: canonical ? canonical.walkInRows : (null as number | null),
      // Bodies present, everyone: advance check-ins + walk-ins.
      attended: canonical ? canonical.attendedTotal : (null as number | null),
      attendedAdvance: canonical
        ? canonical.attendedAdvance
        : (null as number | null),
      attendedWalkIn: canonical
        ? canonical.attendedWalkIn
        : (null as number | null),
      // Attendee RECORDS that show someone present — what the "Attended" filter tab lists.
      attendedRows: canonical
        ? canonical.attendedRows
        : (null as number | null),
      // Advance registrants who checked in (records), distinct from bodies present.
      checkedIn: canonical ? canonical.checkedInRows : (null as number | null),
    }),
    [canonical],
  );

  // Render helper: a canonical count, or a loading/error placeholder (re-audit-4 MEDIUM).
  const canonicalCount = (value: number | null): React.ReactNode =>
    value !== null ? (
      value.toLocaleString()
    ) : summaryError ? (
      <span className="text-red-500" title="Could not load attendance report">
        —
      </span>
    ) : (
      <span className="text-gray-400">…</span>
    );

  // Filter attendees
  const filteredAttendees = React.useMemo(() => {
    if (!attendees) {
      return [];
    }
    if (statusFilter === "all") {
      return attendees;
    }
    // Cohort-based tabs use the canonical per-attendee cohort so each tab's LIST matches its card
    // COUNT (re-audit-3 HIGH). Once the roster has loaded, drive Registered/Walk-ins from it.
    if (cohortById) {
      if (statusFilter === "walk_in") {
        return attendees.filter((a) => cohortById.get(a.id) === "walk_in");
      }
      if (statusFilter === "registered") {
        return attendees.filter((a) => {
          const c = cohortById.get(a.id);
          return c !== undefined && ADVANCE_REGISTRATION_COHORTS.has(c);
        });
      }
      // "Attended" = the canonical PRESENT cohorts (checked_in + walk_in), so the list
      // matches the Attended card instead of re-filtering raw attendance_status — which
      // also swept in `attendance_evidence_incomplete` rows (status 'attended' with NO
      // checked_in_at) that the resolver refuses to count as bodies present.
      if (statusFilter === "attended") {
        return attendees.filter((a) => {
          const c = cohortById.get(a.id);
          return c !== undefined && PRESENT_COHORTS.has(c);
        });
      }
    }
    // Fallbacks while the roster loads (and for statuses with no canonical cohort).
    if (statusFilter === "walk_in") {
      return attendees.filter((a) => a.is_walk_in === true);
    }
    if (statusFilter === "registered") {
      return attendees.filter(
        (a) =>
          !a.is_walk_in &&
          ["registered", "confirmed", "attended", "no_show"].includes(
            a.attendance_status ?? "",
          ),
      );
    }
    return attendees.filter((a) => a.attendance_status === statusFilter);
  }, [attendees, statusFilter, cohortById]);

  const handleCheckIn = async (attendeeId: string) => {
    // If the registrant brought a party (>1 person), confirm the actual headcount
    // via the modal so registered-vs-actual can be verified. Otherwise one-click.
    const att = (attendees as EventAttendee[] | undefined)?.find(
      (a) => a.id === attendeeId,
    );
    const partyTotal =
      (att?.party_member_count || 0) + (att?.party_nonmember_count || 0);
    if (att && partyTotal > 1) {
      setCheckInModalAttendee(att);
      return;
    }
    setCheckingIn(attendeeId);
    try {
      await checkInAttendee(attendeeId);
      await refreshAttendance();
    } catch (error) {
      console.error("Error checking in attendee:", error);
    } finally {
      setCheckingIn(null);
    }
  };

  // Cancel a registration WITHOUT deleting the contact or the attendee row: we set
  // attendance_status='cancelled' so the person still reads as "previously registered,
  // now canceled" in this list, and the attendee-count trigger frees their capacity slot.
  const handleCancelAttendee = async (attendeeId: string, label: string) => {
    if (
      !window.confirm(
        `Cancel this registration for ${label}?\n\nThis removes them from the event roster and frees their spot, but keeps their contact record and their registration history (they will show as "cancelled").`,
      )
    ) {
      return;
    }
    setCancelingId(attendeeId);
    try {
      await updateAttendeeStatus(attendeeId, "cancelled");
      await refreshAttendance();
      showToast("success", "Registration cancelled");
    } catch (error) {
      console.error("Error cancelling attendee:", error);
      showToast("error", "Failed to cancel registration");
    } finally {
      setCancelingId(null);
    }
  };

  const handleConfirmCheckInCounts = async (counts: {
    memberCount: number;
    nonMemberCount: number;
  }) => {
    const att = checkInModalAttendee;
    if (!att) {
      return;
    }
    setCheckingIn(att.id);
    try {
      await checkInAttendee(att.id, counts);
      setCheckInModalAttendee(null);
      await refreshAttendance();
    } catch (error) {
      console.error("Error checking in attendee:", error);
      showToast("error", "Failed to check in attendee");
    } finally {
      setCheckingIn(null);
    }
  };

  // Export the registrant list (with party + actual-attendance counts) to XLSX.
  // The file's counts are canonical (registrantsExport applies the resolver's rules), which
  // needs each row's cohort from get_event_attendance_roster. Refuse to export before that
  // roster has loaded rather than emit a file whose totals contradict the screen.
  const handleExportRegistrants = () => {
    const list = (attendees as EventAttendee[] | undefined) || [];
    if (list.length === 0) {
      showToast("warning", "No registrants to export");
      return;
    }
    if (!cohortById) {
      showToast(
        "warning",
        cohortsError
          ? "Couldn’t load the attendance report, so the export’s attendance counts would not match the page. Retry shortly."
          : "Still loading the attendance report — try the export again in a moment.",
      );
      return;
    }
    const rows = list.map((a) =>
      registrantRowToExport(a, cohortById.get(a.id)),
    );
    const safeTitle = (event.title || "event")
      .replace(/[^a-z0-9]+/gi, "-")
      .replace(/^-|-$/g, "")
      .toLowerCase();
    exportToExcel(rows, {
      filename: `${safeTitle}-registrants`,
      columns: [...REGISTRANT_EXPORT_COLUMNS],
      sheetName: "Registrants",
    });
  };

  // Handle bulk QR code export - opens a printable page
  const handleExportQRCodes = async () => {
    const attendeesWithCodes =
      (attendees as EventAttendee[] | undefined)?.filter(
        (a) => a.checkin_code,
      ) || [];
    if (attendeesWithCodes.length === 0) {
      showToast("warning", "No attendees with check-in codes to export");
      return;
    }

    // Import QRCode dynamically
    const QRCode = (await import("qrcode")).default;

    // Generate QR codes for all attendees
    const qrDataPromises = attendeesWithCodes.map(async (attendee) => {
      // /self-checkin, not the retired /checkin -- see AttendeeQRCodeModal for why.
      const checkinUrl = `${window.location.origin}/self-checkin?code=${attendee.checkin_code}`;
      const dataUrl = await QRCode.toDataURL(checkinUrl, {
        width: 200,
        margin: 1,
      });
      return {
        name: attendee.display_name || attendee.email || "Unknown",
        email: attendee.contact_email || attendee.email || "",
        code: attendee.checkin_code,
        qrDataUrl: dataUrl,
      };
    });

    const qrData = await Promise.all(qrDataPromises);

    // Create printable HTML
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      showToast("error", "Please allow popups to export QR codes");
      return;
    }

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>QR Codes - ${event.title}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 20px; }
          h1 { text-align: center; margin-bottom: 10px; font-size: 24px; }
          .event-info { text-align: center; color: #666; margin-bottom: 30px; }
          .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
          .card { border: 1px solid #e5e7eb; border-radius: 8px; padding: 16px; text-align: center; break-inside: avoid; }
          .card img { width: 150px; height: 150px; margin-bottom: 12px; }
          .name { font-weight: 600; font-size: 14px; color: #111; margin-bottom: 4px; }
          .email { font-size: 12px; color: #666; margin-bottom: 8px; word-break: break-all; }
          .code { font-family: monospace; font-size: 14px; font-weight: 600; color: #4f46e5; }
          @media print {
            .no-print { display: none; }
            .grid { grid-template-columns: repeat(3, 1fr); }
            .card { break-inside: avoid; page-break-inside: avoid; }
          }
          .print-btn { position: fixed; top: 20px; right: 20px; padding: 10px 20px; background: #4f46e5; color: white; border: none; border-radius: 6px; cursor: pointer; font-size: 14px; }
          .print-btn:hover { background: #4338ca; }
        </style>
      </head>
      <body>
        <button class="print-btn no-print" onclick="window.print()">Print / Save as PDF</button>
        <h1>${event.title}</h1>
        <p class="event-info">${qrData.length} Check-in QR Codes</p>
        <div class="grid">
          ${qrData
            .map(
              (item) => `
            <div class="card">
              <img src="${item.qrDataUrl}" alt="QR Code for ${item.name}" />
              <div class="name">${item.name}</div>
              ${item.email ? `<div class="email">${item.email}</div>` : ""}
              <div class="code">${item.code}</div>
            </div>
          `,
            )
            .join("")}
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
    showToast(
      "success",
      `Exported ${qrData.length} QR codes. Use Print/Save as PDF to save.`,
    );
  };

  // Membership badge for the attendee's linked contact. IMPORTANT: is_usls_member=true does
  // NOT imply active — the largest cohort is canceled members that still carry the flag — so
  // member_status wins. Buckets: Canceled Member (was a member, now lapsed) / Active Member /
  // Non-Member (no linked/flagged membership). Values are matched case-insensitively because the
  // column holds both "canceled" and "Canceled".
  const getMembershipStatus = (attendee: {
    contact_id?: string | null;
    contacts?: {
      is_usls_member?: boolean | null;
      member_status?: string | null;
    } | null;
  }): { label: string; className: string } => {
    const c = attendee.contacts;
    const status = (c?.member_status || "").trim().toLowerCase();
    if (status === "canceled" || status === "cancelled") {
      return {
        label: "Canceled Member",
        className: "bg-amber-100 text-amber-800",
      };
    }
    if (status === "active" || c?.is_usls_member) {
      return {
        label: "Active Member",
        className: "bg-green-100 text-green-800",
      };
    }
    return { label: "Non-Member", className: "bg-gray-100 text-gray-600" };
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "proposed":
        return "bg-gray-100 text-gray-800";
      case "invited":
        return "bg-blue-100 text-blue-800";
      case "registered":
        return "bg-green-100 text-green-800";
      case "attended":
        return "bg-purple-100 text-purple-800";
      case "no_show":
        return "bg-red-100 text-red-800";
      case "cancelled":
        return "bg-gray-100 text-gray-500";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getRSVPColor = (rsvp: string | null) => {
    switch (rsvp) {
      case "yes":
        return "text-green-600";
      case "no":
        return "text-red-600";
      case "maybe":
        return "text-yellow-600";
      default:
        return "text-gray-400";
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* View Toggle + Action Buttons */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        {/* List / Check-in Feed toggle */}
        <div className="flex bg-gray-100 rounded-lg p-0.5">
          <button
            onClick={() => setAttendeeView("list")}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
              attendeeView === "list"
                ? "bg-white shadow text-gray-900"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Attendee List
          </button>
          <button
            onClick={() => setAttendeeView("checkin-feed")}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
              attendeeView === "checkin-feed"
                ? "bg-white shadow text-gray-900"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Check-in Feed
          </button>
          <button
            onClick={() => setAttendeeView("attendance-report")}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
              attendeeView === "attendance-report"
                ? "bg-white shadow text-gray-900"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Attendance &amp; No-shows
          </button>
        </div>

        <div className="flex flex-wrap gap-3">
          {attendees && (attendees as EventAttendee[]).length > 0 && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleExportRegistrants()}
              className="flex items-center gap-2"
            >
              <DocumentDuplicateIcon className="h-4 w-4" />
              Export Registrants
            </Button>
          )}
          {attendees &&
            (attendees as EventAttendee[]).some((a) => a.checkin_code) && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handleExportQRCodes()}
                className="flex items-center gap-2"
              >
                <DocumentDuplicateIcon className="h-4 w-4" />
                Export QR Codes
              </Button>
            )}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowListModal(true)}
            className="flex items-center gap-2"
          >
            <ClipboardDocumentListIcon className="h-4 w-4" />
            Link List
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowUploadModal(true)}
            className="flex items-center gap-2"
          >
            <UserPlusIcon className="h-4 w-4" />
            Import CSV
          </Button>
        </div>
      </div>

      {/* Check-in Feed View */}
      {attendeeView === "checkin-feed" && <CheckinsTab eventId={eventId} />}

      {/* Attendance & No-shows report */}
      {attendeeView === "attendance-report" && (
        <AttendanceReportView eventId={eventId} event={event} />
      )}

      {/* Attendee List View */}
      {attendeeView === "list" && (
        <>
          {/* Linked Lists */}
          {linkedLists && linkedLists.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-800 mb-2">
                Linked Lists
              </h4>
              <div className="flex flex-wrap gap-2">
                {sortEventListRows(linkedLists as EventListLink[]).map(
                  (link) =>
                    isEibAudienceLink(link) ? (
                      // Created by the Event in a Box launch: its frozen invitation audience. Read-only:
                      // no unlink (the database keeps it anyway) -- it is the record of who was invited.
                      <span
                        key={link.list_id}
                        data-testid="eib-audience-link"
                        className="inline-flex items-center gap-2 bg-white px-3 py-1 rounded-full text-sm border border-indigo-300"
                        title={`${link.lists?.name ?? "Snapshot list"} — created by the Event in a Box launch. It is the frozen invitation audience and cannot be unlinked or edited.`}
                      >
                        <ClipboardDocumentListIcon className="h-4 w-4 text-indigo-600" />
                        <span className="text-gray-900">
                          {eibAudienceLabel(link)}
                        </span>
                        {/* The list name tells launches apart (an event can have an original launch and
                            radius expansions, each with its own frozen audience). */}
                        <span className="max-w-xs truncate text-xs text-gray-500">
                          {link.lists?.name}
                        </span>
                        <Link
                          to={segmentHref(link.list_id)}
                          className="text-xs font-medium text-primary-700 hover:text-primary-900 underline"
                        >
                          View segment
                        </Link>
                      </span>
                    ) : (
                      <span
                        key={link.list_id}
                        className="inline-flex items-center gap-2 bg-white px-3 py-1 rounded-full text-sm border border-blue-200"
                      >
                        <ClipboardDocumentListIcon className="h-4 w-4 text-blue-600" />
                        {link.lists?.name || "Unknown List"}
                        <button
                          onClick={() => handleUnlinkList(link.list_id)}
                          className="text-gray-400 hover:text-red-500"
                          title="Unlink list"
                        >
                          <XCircleIcon className="h-4 w-4" />
                        </button>
                      </span>
                    ),
                )}
              </div>
            </div>
          )}

          {/* Capacity Dashboard. Proposed and Invited cards removed 2026-09-06: both read
              attendance_status values the CHECK constraint forbids, so both were permanently 0.
              Invitation volume is a campaign-flight number, shown in the Overview funnel. */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <p className="text-sm font-medium text-green-600">Registered</p>
              <p className="text-2xl font-bold text-green-900 mt-1">
                {canonicalCount(metrics.registered)}
              </p>
              <p className="text-xs text-green-700 mt-0.5">
                {canonicalCount(canonical?.registeredHeadcount ?? null)} people,
                in advance
              </p>
            </div>
            <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
              <p className="text-sm font-medium text-purple-600">Attended</p>
              <p className="text-2xl font-bold text-purple-900 mt-1">
                {canonicalCount(metrics.attended)}
              </p>
              {/* Walk-ins are a labelled SUBSET of the total, never hidden inside it. */}
              <p className="text-xs text-purple-700 mt-0.5">
                {canonicalCount(metrics.attendedAdvance)} registered +{" "}
                {canonicalCount(metrics.attendedWalkIn)} walk-in
              </p>
            </div>
            <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
              <p className="text-sm font-medium text-indigo-600">Checked In</p>
              <p className="text-2xl font-bold text-indigo-900 mt-1">
                {canonicalCount(metrics.checkedIn)}
              </p>
              <p className="text-xs text-indigo-700 mt-0.5">
                of {canonicalCount(metrics.registered)} registrations
              </p>
            </div>
            <div className="bg-teal-50 rounded-lg p-4 border border-teal-200">
              <p className="text-sm font-medium text-teal-600">Walk-ins</p>
              <p className="text-2xl font-bold text-teal-900 mt-1">
                {canonicalCount(metrics.walkIns)}
              </p>
              <p className="text-xs text-teal-700 mt-0.5">
                {canonicalCount(metrics.attendedWalkIn)} people
              </p>
            </div>
          </div>

          {/* Link List Modal */}
          {showListModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Link a List
                  </h3>
                  <button
                    onClick={() => setShowListModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
                {availableLists && availableLists.length > 0 ? (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {(availableLists as ListItem[]).map((list) => {
                      const isLinked = (
                        linkedLists as EventListLink[] | undefined
                      )?.some((l) => l.list_id === list.id);
                      return (
                        <button
                          key={list.id}
                          onClick={() => !isLinked && handleLinkList(list.id)}
                          disabled={isLinked}
                          className={`w-full text-left p-3 rounded-lg border transition-colors ${
                            isLinked
                              ? "bg-gray-100 border-gray-200 cursor-not-allowed"
                              : "border-gray-200 hover:bg-primary-50 hover:border-primary-300"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-gray-900">
                                {list.name}
                              </p>
                              {list.description && (
                                <p className="text-sm text-gray-500">
                                  {list.description}
                                </p>
                              )}
                            </div>
                            {isLinked && (
                              <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded">
                                Linked
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">
                    No lists available
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Upload CSV Modal */}
          {showUploadModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Import Attendees from CSV
                  </h3>
                  <button
                    onClick={() => setShowUploadModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Upload a CSV file with attendee information. The file should
                    have columns for:
                  </p>
                  <ul className="text-sm text-gray-600 list-disc list-inside">
                    <li>
                      <strong>email</strong> (required)
                    </li>
                    <li>first_name or first name</li>
                    <li>last_name or last name</li>
                  </ul>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="csv-upload"
                    />
                    <label
                      htmlFor="csv-upload"
                      className="cursor-pointer flex flex-col items-center"
                    >
                      <DocumentIcon className="h-12 w-12 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-600">
                        Click to select CSV file
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Filter Tabs — buttons scroll horizontally on narrow (phone) widths instead of overflowing */}
          <div className="flex items-center gap-2 border-b border-gray-200">
            <FunnelIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
            <div className="flex gap-1 overflow-x-auto">
              {[
                { id: "all", label: "All", count: attendees?.length || 0 },
                // "Proposed" and "Invited" removed 2026-09-06: both filtered on attendance_status
                // values the CHECK constraint forbids, so both were permanently 0 and selecting
                // them showed an empty list. Invitation volume for an EIB event lives on the
                // campaign flights, not on event_attendees, and is shown in the Overview funnel.
                {
                  id: "registered",
                  label: "Registered",
                  count: metrics.registered,
                },
                // Record count (this tab lists RECORDS), but drawn from the canonical
                // present cohorts — not a raw attendance_status filter.
                {
                  id: "attended",
                  label: "Attended",
                  count: metrics.attendedRows,
                },
                { id: "walk_in", label: "Walk-ins", count: metrics.walkIns },
              ].map(({ id, label, count }) => (
                <button
                  key={id}
                  onClick={() => setStatusFilter(id)}
                  className={`flex-shrink-0 whitespace-nowrap px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    statusFilter === id
                      ? "border-primary-500 text-primary-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  {label} (
                  {typeof count === "number" ? count : summaryError ? "—" : "…"}
                  )
                </button>
              ))}
            </div>
          </div>

          {/* Attendee List. Registered/Attended/Walk-ins are canonical-cohort tabs: while the
              roster loads (or if it errors) show that state rather than a raw-status list that
              wouldn't match the card count (re-audit-4 MEDIUM). */}
          {["registered", "attended", "walk_in"].includes(statusFilter) &&
          !cohortById ? (
            <div className="text-center py-12 text-gray-500">
              {cohortsError
                ? "Couldn’t load the attendance report — counts and this list are unavailable. Retry shortly."
                : "Loading attendance report…"}
            </div>
          ) : filteredAttendees.length === 0 ? (
            <div className="text-center py-12">
              <UserGroupIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No attendees found
              </h3>
              <p className="text-gray-500">
                {statusFilter === "all"
                  ? "No attendees have been added to this event yet."
                  : `No attendees with status "${statusFilter}".`}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredAttendees.map((attendee) => (
                <div
                  key={attendee.id}
                  className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      {/* Check-in Status Icon */}
                      <div>
                        {attendee.checked_in_at ? (
                          <CheckCircleIconSolid className="h-8 w-8 text-green-500" />
                        ) : (
                          <UserGroupIcon className="h-8 w-8 text-gray-300" />
                        )}
                      </div>

                      {/* Attendee Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h4 className="text-base font-semibold text-gray-900 break-words">
                            {`${attendee.first_name || attendee.contacts?.first_name || ""} ${attendee.last_name || attendee.contacts?.last_name || ""}`.trim() ||
                              attendee.display_name ||
                              attendee.email}
                          </h4>
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(attendee.attendance_status)}`}
                          >
                            {attendee.attendance_status}
                          </span>
                          {(() => {
                            const m = getMembershipStatus(attendee);
                            return (
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-medium ${m.className}`}
                              >
                                {m.label}
                              </span>
                            );
                          })()}
                          {attendee.invited_by_contact_id &&
                            attendee.inviter && (
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                guest of{" "}
                                {`${attendee.inviter.first_name || ""} ${attendee.inviter.last_name || ""}`.trim() ||
                                  "a member"}
                              </span>
                            )}
                        </div>

                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-gray-500 break-words">
                          {(() => {
                            const email =
                              attendee.email || attendee.contacts?.email;
                            if (!email) {
                              return null;
                            }
                            // Clickable email → the person's Contact page when we have a
                            // linked contact_id; otherwise plain text.
                            return attendee.contact_id ? (
                              <Link
                                to={`/contacts/${attendee.contact_id}`}
                                className="text-blue-600 hover:text-blue-800 hover:underline"
                                title="Open contact record"
                              >
                                {email}
                              </Link>
                            ) : (
                              <span>{email}</span>
                            );
                          })()}
                          {(attendee.phone ||
                            attendee.contacts?.phone_number ||
                            attendee.contacts?.mobile_phone) && (
                            <span>
                              {attendee.phone ||
                                attendee.contacts?.phone_number ||
                                attendee.contacts?.mobile_phone}
                            </span>
                          )}
                          {attendee.organization_name && (
                            <span className="flex items-center gap-1">
                              <BuildingOfficeIcon className="h-4 w-4" />
                              {attendee.organization_name}
                            </span>
                          )}
                        </div>

                        {/* RSVP and Guest Count */}
                        <div className="flex items-center gap-4 mt-2">
                          {attendee.rsvp_response && (
                            <span className="text-sm">
                              <span className="text-gray-500">RSVP:</span>{" "}
                              <span
                                className={`font-medium ${getRSVPColor(attendee.rsvp_response)}`}
                              >
                                {attendee.rsvp_response.toUpperCase()}
                              </span>
                            </span>
                          )}
                          {attendee.guest_count > 0 && (
                            <span className="text-sm text-gray-500">
                              +{attendee.guest_count}{" "}
                              {attendee.guest_count === 1 ? "guest" : "guests"}
                            </span>
                          )}
                          {attendee.checked_in_at && (
                            <span className="text-sm text-gray-500">
                              Checked in: {formatTime(attendee.checked_in_at)}
                            </span>
                          )}
                        </div>

                        {/* Registration Details */}
                        {attendee.registration_source && (
                          <div className="mt-2 text-xs text-gray-400">
                            Registered via: {attendee.registration_source}
                            {attendee.registration_date &&
                              ` on ${formatDate(attendee.registration_date)}`}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2">
                      {/* QR Code Button - show for all attendees with a checkin_code */}
                      {attendee.checkin_code && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() =>
                            setQrModalAttendee({
                              name:
                                attendee.display_name ||
                                attendee.email ||
                                "Unknown",
                              email: attendee.contact_email || attendee.email,
                              checkinCode: attendee.checkin_code,
                            })
                          }
                          className="flex items-center gap-2"
                          title="View Check-in QR Code"
                        >
                          <QrCodeIcon className="h-4 w-4" />
                          <span className="hidden sm:inline">QR</span>
                        </Button>
                      )}

                      {/* Check-in Button */}
                      {!attendee.checked_in_at &&
                        attendee.attendance_status === "registered" && (
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleCheckIn(attendee.id)}
                            disabled={checkingIn === attendee.id}
                            className="flex items-center gap-2"
                          >
                            <CheckCircleIcon className="h-4 w-4" />
                            {checkingIn === attendee.id
                              ? "Checking in..."
                              : "Check In"}
                          </Button>
                        )}

                      {/* Cancel Registration — keeps the contact + a "cancelled"
                          history row; only offered while the person is still an
                          active registrant (not already cancelled, not checked in). */}
                      {attendee.attendance_status !== "cancelled" &&
                        !attendee.checked_in_at && (
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() =>
                              handleCancelAttendee(
                                attendee.id,
                                `${attendee.first_name || attendee.contacts?.first_name || ""} ${attendee.last_name || attendee.contacts?.last_name || ""}`.trim() ||
                                  attendee.email ||
                                  "this attendee",
                              )
                            }
                            disabled={cancelingId === attendee.id}
                            className="flex items-center gap-2"
                            title="Cancel this registration"
                          >
                            <XMarkIcon className="h-4 w-4" />
                            {cancelingId === attendee.id
                              ? "Cancelling..."
                              : "Cancel"}
                          </Button>
                        )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* QR Code Modal */}
      <AttendeeQRCodeModal
        isOpen={!!qrModalAttendee}
        onClose={() => setQrModalAttendee(null)}
        attendeeName={qrModalAttendee?.name || ""}
        attendeeEmail={qrModalAttendee?.email}
        checkinCode={qrModalAttendee?.checkinCode || ""}
        eventTitle={event.title}
      />
      {checkInModalAttendee && (
        <CheckInCountsModal
          name={
            checkInModalAttendee.display_name ||
            [checkInModalAttendee.first_name, checkInModalAttendee.last_name]
              .filter(Boolean)
              .join(" ") ||
            checkInModalAttendee.email ||
            "Registrant"
          }
          registeredMembers={checkInModalAttendee.party_member_count || 0}
          registeredNonMembers={checkInModalAttendee.party_nonmember_count || 0}
          busy={checkingIn === checkInModalAttendee.id}
          onConfirm={handleConfirmCheckInCounts}
          onCancel={() => setCheckInModalAttendee(null)}
        />
      )}
    </div>
  );
};

const DocumentsTab: React.FC<{ eventId: string }> = ({ eventId }) => {
  const [newComment, setNewComment] = useState("");
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  // Get current user ID when component mounts
  React.useEffect(() => {
    const loadUser = async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        setCurrentUserId(user?.id || null);
      } catch (error) {
        console.error("Error loading user:", error);
      }
    };
    loadUser();
  }, []);

  // Fetch comments
  const {
    data: comments,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["event-comments", eventId],
    queryFn: () => getEventComments(eventId),
  });

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) {
      return;
    }

    setSubmitting(true);
    try {
      await createEventComment(eventId, newComment.trim());
      setNewComment("");
      await refetch();
    } catch (error) {
      console.error("Error creating comment:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleStartEdit = (comment: EventComment) => {
    setEditingCommentId(comment.id);
    setEditText(comment.comment_text);
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditText("");
  };

  const handleSaveEdit = async (commentId: string) => {
    if (!editText.trim()) {
      return;
    }

    setSubmitting(true);
    try {
      await updateEventComment(commentId, editText.trim());
      setEditingCommentId(null);
      setEditText("");
      await refetch();
    } catch (error) {
      console.error("Error updating comment:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!confirm("Are you sure you want to delete this comment?")) {
      return;
    }

    try {
      await deleteEventComment(commentId);
      await refetch();
    } catch (error) {
      console.error("Error deleting comment:", error);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) {
      return "just now";
    }
    if (diffMins < 60) {
      return `${diffMins}m ago`;
    }
    if (diffHours < 24) {
      return `${diffHours}h ago`;
    }
    if (diffDays < 7) {
      return `${diffDays}d ago`;
    }
    return formatDate(timestamp);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* New Comment Form */}
      <form
        onSubmit={handleSubmitComment}
        className="bg-gray-50 rounded-lg p-4 border border-gray-200"
      >
        <div className="flex items-start gap-3">
          <UserCircleIcon className="h-10 w-10 text-gray-400 flex-shrink-0 mt-1" />
          <div className="flex-1">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Add a comment... (use @ to mention teammates)"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
            />
            <div className="flex justify-end mt-2">
              <Button
                type="submit"
                variant="primary"
                size="sm"
                disabled={!newComment.trim() || submitting}
                className="flex items-center gap-2"
              >
                <PaperAirplaneIcon className="h-4 w-4" />
                {submitting ? "Posting..." : "Post Comment"}
              </Button>
            </div>
          </div>
        </div>
      </form>

      {/* Comments List */}
      {!comments || comments.length === 0 ? (
        <div className="text-center py-12">
          <ChatBubbleLeftIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No comments yet
          </h3>
          <p className="text-gray-500">Be the first to comment on this event</p>
        </div>
      ) : (
        <div className="space-y-4">
          {comments.map((comment) => {
            const isEditing = editingCommentId === comment.id;
            const isAuthor = currentUserId === comment.created_by;

            return (
              <div
                key={comment.id}
                className="bg-white border border-gray-200 rounded-lg p-4"
              >
                <div className="flex items-start gap-3">
                  <UserCircleIcon className="h-10 w-10 text-gray-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    {/* Author and Timestamp */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900">
                          {comment.created_by_profile?.full_name ||
                            comment.created_by_profile?.email ||
                            "Unknown"}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatTimestamp(comment.created_at)}
                          {comment.is_edited && " (edited)"}
                        </span>
                      </div>

                      {/* Edit/Delete Buttons */}
                      {isAuthor && !isEditing && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleStartEdit(comment)}
                            className="text-gray-500 hover:text-primary-600 p-1"
                            title="Edit comment"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteComment(comment.id)}
                            className="text-gray-500 hover:text-red-600 p-1"
                            title="Delete comment"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Comment Content */}
                    {isEditing ? (
                      <div className="space-y-2">
                        <textarea
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                        />
                        <div className="flex gap-2">
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleSaveEdit(comment.id)}
                            disabled={!editText.trim() || submitting}
                          >
                            {submitting ? "Saving..." : "Save"}
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={handleCancelEdit}
                            disabled={submitting}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-700 whitespace-pre-wrap break-words">
                        {comment.comment_text}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const TasksTab: React.FC<{ eventId: string }> = () => {
  return (
    <div className="text-center py-12">
      <ClipboardDocumentListIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
      <h3 className="text-lg font-medium text-gray-900 mb-2">Tasks</h3>
      <p className="text-gray-500 mb-4">
        Task management integration will be added in Phase 2
      </p>
    </div>
  );
};

// onGoToSettings is REQUIRED, not optional: this component previously called setActiveTab
// directly, which is declared in EventDetail's scope and not here. tsc reported "Cannot find name
// 'setActiveTab'" and the button threw a ReferenceError on click whenever an event had no
// reward-configured CTA. Same class as the 2026-06-18 isLocked production crash. It also targeted
// "settings", which was never a TabType — so it pointed nowhere even before the scope error.
const VouchersTab: React.FC<{
  eventId: string;
  onGoToSettings: () => void;
}> = ({ eventId, onGoToSettings }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  // Reward activity logging is non-blocking: the reward is already issued, so a failed log is a
  // warning toast, never an error on the reward itself.
  const warnRewardNotLogged = (message: string) =>
    showToast("warning", message, 8000);
  const [statusFilter, setStatusFilter] = useState<
    "all" | "redeemed" | "unredeemed"
  >("all");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [modalStep, setModalStep] = useState<"form" | "confirm" | "success">(
    "form",
  );
  const [createEmail, setCreateEmail] = useState("");
  const [selectedCtaId, setSelectedCtaId] = useState<string>("");
  const [lookupContact, setLookupContact] = useState<Contact | null>(null);
  const [lookupStatus, setLookupStatus] = useState<
    "idle" | "loading" | "found" | "not_found"
  >("idle");
  const [recentVouchers, setRecentVouchers] = useState<any[]>([]);
  const [issueNotes, setIssueNotes] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createdVoucherCode, setCreatedVoucherCode] = useState<string>("");
  const [sendVoucherEmail, setSendVoucherEmail] = useState(true); // Pre-checked by default
  const [sendVoucherText, setSendVoucherText] = useState(false); // Not pre-checked
  const [copySuccess, setCopySuccess] = useState(false);

  // Fetch event details to get voucher fields
  const { data: event } = useQuery({
    queryKey: ["events", eventId],
    queryFn: () => getEventById(eventId),
    enabled: !!eventId,
  });

  // Fetch CTAs for this event
  const { data: ctas = [] } = useQuery({
    queryKey: ["event-ctas", eventId],
    queryFn: () => getEventCTAs(eventId),
    enabled: !!eventId,
  });

  // Fetch vouchers
  const {
    data: vouchers,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["event-vouchers", eventId],
    queryFn: () => getEventVouchers(eventId),
  });

  // Auto-select CTA when there's only one with rewards
  React.useEffect(() => {
    const ctasWithRewardsLocal = ctas.filter(
      (c) => c.reward_type && c.reward_type !== "none",
    );
    if (ctasWithRewardsLocal.length === 1 && !selectedCtaId) {
      setSelectedCtaId(ctasWithRewardsLocal[0].id);
    }
  }, [ctas, selectedCtaId]);

  // Calculate voucher metrics
  const metrics = React.useMemo(() => {
    if (!vouchers) {
      return { total: 0, redeemed: 0, unredeemed: 0, totalValue: 0 };
    }

    const redeemed = vouchers.filter((v) => v.is_redeemed).length;
    // For total value: use applied_amount for redeemed vouchers, discount_value for unredeemed
    const totalValue = vouchers.reduce(
      (sum, v) =>
        sum +
        (v.is_redeemed
          ? v.applied_amount || v.discount_value || 0
          : v.discount_value || 0),
      0,
    );

    return {
      total: vouchers.length,
      redeemed,
      unredeemed: vouchers.length - redeemed,
      totalValue,
    };
  }, [vouchers]);

  // Filter vouchers
  const filteredVouchers = React.useMemo(() => {
    if (!vouchers) {
      return [];
    }
    if (statusFilter === "all") {
      return vouchers;
    }
    if (statusFilter === "redeemed") {
      return vouchers.filter((v) => v.is_redeemed);
    }
    return vouchers.filter((v) => !v.is_redeemed);
  }, [vouchers, statusFilter]);

  // Get the selected CTA details
  const selectedCta = ctas.find((c) => c.id === selectedCtaId);

  // Check if there are CTAs with rewards configured (not 'none')
  const ctasWithRewards = ctas.filter(
    (c) => c.reward_type && c.reward_type !== "none",
  );
  const hasCtasWithRewards = ctasWithRewards.length > 0;

  // Get member status badge.
  // Authoritative membership = an active Zuora subscription (has_active_subscription).
  // "Active Member" is shown ONLY when that flag is true — a lapsed contact still
  // carrying a "Member" label is NEVER labeled active. Lower branches surface prior/
  // label context (Inactive/Expired/Prospect) so an operator still gets useful signal.
  // EIB member-identification standardization, 2026-07-30.
  const getMemberStatusBadge = (
    contact: Contact | null,
  ): { label: string; color: string } => {
    if (!contact) {
      return { label: "Other", color: "bg-gray-500" };
    }
    if (contact.has_active_subscription === true) {
      return { label: "Active Member", color: "bg-green-500" };
    }
    // No active subscription below this point → never "Active Member".
    if (
      contact.contact_types?.includes("Expired Member") ||
      contact.contact_type === "Expired Member" ||
      (contact.is_usls_member && contact.member_status === "inactive")
    ) {
      return { label: "Expired Member", color: "bg-orange-500" };
    }
    if (
      contact.is_usls_member ||
      contact.contact_types?.includes("Member") ||
      contact.contact_type === "Member"
    ) {
      // Has a member label but no active subscription → lapsed, not current.
      return { label: "Inactive Member", color: "bg-yellow-500" };
    }
    if (
      contact.contact_types?.includes("Prospect") ||
      contact.contact_type === "Prospect"
    ) {
      return { label: "Prospect", color: "bg-blue-500" };
    }
    return { label: "Other", color: "bg-gray-500" };
  };

  // Handle email lookup
  const handleEmailLookup = async () => {
    if (!createEmail.trim()) {
      return;
    }

    setLookupStatus("loading");
    setLookupContact(null);
    setRecentVouchers([]);

    try {
      const contact = await getContactByEmail(createEmail.trim());
      if (contact) {
        setLookupContact(contact);
        setLookupStatus("found");
        // Get recent vouchers for this contact
        const recent = await getRecentVouchersForContact(contact.id, 30);
        setRecentVouchers(recent);
      } else {
        setLookupStatus("not_found");
      }
    } catch (error) {
      console.error("Error looking up contact:", error);
      setLookupStatus("not_found");
    }
  };

  // Handle proceed to confirmation
  const handleProceedToConfirm = (e: React.FormEvent) => {
    e.preventDefault();
    if (!createEmail.trim()) {
      return;
    }

    // Check for duplicate email for this CTA (or event if no CTA selected)
    // If a specific CTA is selected, check for duplicate within that CTA
    // Otherwise, check for duplicate within the entire event
    const existingVoucher = vouchers?.find((v) => {
      const emailMatch =
        v.email.toLowerCase() === createEmail.toLowerCase().trim();
      if (!emailMatch) {
        return false;
      }

      // If a CTA is selected, only match vouchers for the same CTA
      if (selectedCtaId) {
        return v.cta_id === selectedCtaId;
      }
      // If no CTA selected, match any voucher for this email
      return true;
    });

    if (existingVoucher) {
      const ctaName = selectedCta?.name || selectedCta?.title;
      setCreateError(
        selectedCtaId && ctaName
          ? `A reward has already been issued to this email address for the "${ctaName}" Call to Action.`
          : "A voucher already exists for this email address for this event.",
      );
      return;
    }

    setCreateError(null);
    setModalStep("confirm");
  };

  // Handle issue reward - supports vouchers, URL offers, and physical goods
  const handleCreateVoucher = async () => {
    if (!createEmail.trim() || !event) {
      return;
    }

    // Validate event end date - cannot issue vouchers for events that have ended
    if (event.end_time) {
      const eventEndDate = new Date(event.end_time);
      if (eventEndDate < new Date()) {
        setCreateError(
          "Cannot issue rewards for events that have already ended.",
        );
        return;
      }
    }

    setIsCreating(true);
    setCreateError(null);

    const rewardType = selectedCta?.reward_type || "voucher";

    try {
      // For voucher rewards, call generate-offer edge function
      if (rewardType === "voucher" || rewardType === "none" || !rewardType) {
        // Build the request payload for generate-offer
        const payload: Record<string, unknown> = {
          email: createEmail.trim(),
          eventId: eventId,
        };

        // Add CTA if selected
        if (selectedCtaId && selectedCta) {
          payload.ctaId = selectedCtaId;
        }

        // Add contact_id if we found a contact
        if (lookupContact) {
          payload.contact_id = lookupContact.id;
        }

        // Add issue notes if provided
        if (issueNotes.trim()) {
          payload.issue_notes = issueNotes.trim();
        }

        // Call generate-offer edge function
        const { data, error } = await supabase.functions.invoke(
          "generate-offer",
          {
            body: payload,
          },
        );

        if (error) {
          throw error;
        }

        if (!data.status) {
          throw new Error(data.message || "Failed to generate voucher");
        }

        // Extract voucher data from nested response
        const voucherData = data.data;
        const voucherCode = voucherData?.voucherCode;

        if (!voucherCode) {
          throw new Error("Voucher was created but code was not returned");
        }

        // Log the interaction if we have a contact. generate-offer returns the voucher id as
        // voucherId (main path) or fulfillmentId (offer/CTA-junction paths) — never voucher_id.
        if (lookupContact && user) {
          const contactId = lookupContact.id;
          const userId = user.id;
          await recordRewardActivity(
            () =>
              logVoucherIssued({
                contactId,
                userId,
                eventId: eventId,
                ctaId: selectedCtaId || undefined,
                voucherId:
                  voucherData?.voucherId ?? voucherData?.fulfillmentId ?? null,
                voucherCode: voucherCode,
                voucherAmount:
                  selectedCta?.voucher_amount || event.voucher_amount || 0,
                notes: issueNotes.trim() || undefined,
              }),
            warnRewardNotLogged,
          );
        }

        // Send voucher email to recipient (if checkbox is checked)
        if (sendVoucherEmail) {
          try {
            const voucherAmount =
              selectedCta?.voucher_amount || event.voucher_amount || 0;
            const voucherName = selectedCta?.name || event.title || "Voucher";

            // Format expiration date if available
            let expirationDate: string | undefined;
            if (selectedCta?.voucher_end_date) {
              expirationDate = formatDate(
                new Date(selectedCta.voucher_end_date),
              );
            } else if (event.end_time) {
              expirationDate = formatDate(new Date(event.end_time));
            }

            const { error: emailError } = await supabase.functions.invoke(
              "send-email",
              {
                body: {
                  type: "voucher",
                  to: createEmail.trim(),
                  toName: lookupContact
                    ? `${lookupContact.first_name} ${lookupContact.last_name}`.trim()
                    : undefined,
                  voucherCode: voucherCode,
                  voucherAmount: voucherAmount,
                  voucherName: voucherName,
                  qrImageUrl: voucherData?.qrImageUrl,
                  eventName: event.title,
                  expirationDate: expirationDate,
                },
              },
            );

            if (emailError) {
              console.error("Failed to send voucher email:", emailError);
              // Don't fail the operation - voucher was created successfully
            } else {
              console.log("Voucher email sent successfully to:", createEmail);
            }
          } catch (emailError) {
            console.error("Error sending voucher email:", emailError);
            // Don't fail the operation - voucher was created successfully
          }
        }

        // TODO: Send voucher via SMS/Text when sendVoucherText is checked
        // This will be implemented when SMS integration is added

        // Success - show the voucher code
        setCreatedVoucherCode(voucherCode);
        setModalStep("success");
        await refetch();
      }
      // For URL offers, just log the interaction and show the URL
      else if (rewardType === "url_offer") {
        const offerUrl =
          selectedCta?.generated_url ||
          selectedCta?.offer_url ||
          selectedCta?.base_url ||
          "";

        if (!offerUrl) {
          throw new Error("No URL configured for this offer");
        }

        // Log the interaction if we have a contact
        if (lookupContact && user) {
          const contactId = lookupContact.id;
          const userId = user.id;
          await recordRewardActivity(
            () =>
              logOfferLinkShared({
                contactId,
                userId,
                eventId: eventId,
                ctaId: selectedCtaId || undefined,
                url: offerUrl,
                ctaName: selectedCta?.name || selectedCta?.title || "Offer",
                sentViaEmail: false, // For now, just copying - email will be phase 2
                notes: issueNotes.trim() || undefined,
              }),
            warnRewardNotLogged,
          );
        }

        // Success - show the URL
        setCreatedVoucherCode(offerUrl);
        setModalStep("success");
      }
      // For physical goods, log the interaction
      else if (rewardType === "physical_good") {
        if (!lookupContact) {
          throw new Error("Contact information is required for physical goods");
        }

        // Log the interaction
        if (user) {
          const contactId = lookupContact.id;
          const userId = user.id;
          await recordRewardActivity(
            () =>
              logPhysicalDistributed({
                contactId,
                userId,
                eventId: eventId,
                ctaId: selectedCtaId || undefined,
                itemName: selectedCta?.name || selectedCta?.title || "Item",
                distributionMethod: "handed_out",
                notes: issueNotes.trim() || undefined,
              }),
            warnRewardNotLogged,
          );
        }

        // Success
        setCreatedVoucherCode("DISTRIBUTED");
        setModalStep("success");
      }
      // For other reward types
      else {
        throw new Error(`Unsupported reward type: ${rewardType}`);
      }
    } catch (error) {
      console.error("Error issuing reward:", error);
      setCreateError(
        error instanceof Error ? error.message : "Failed to issue reward",
      );
    } finally {
      setIsCreating(false);
    }
  };

  // Reset modal state
  const resetModal = () => {
    setShowCreateModal(false);
    setModalStep("form");
    setCreateEmail("");
    setSelectedCtaId("");
    setLookupContact(null);
    setLookupStatus("idle");
    setRecentVouchers([]);
    setIssueNotes("");
    setCreateError(null);
    setCreatedVoucherCode("");
    setCopySuccess(false);
    setSendVoucherEmail(true); // Reset to default (checked)
    setSendVoucherText(false); // Reset to default (unchecked)
  };

  // Copy voucher code to clipboard
  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(createdVoucherCode);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error("Failed to copy:", error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <p className="text-sm font-medium text-blue-600">Total Vouchers</p>
          <p className="text-2xl font-bold text-blue-900 mt-1">
            {metrics.total}
          </p>
        </div>
        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
          <p className="text-sm font-medium text-green-600">Redeemed</p>
          <p className="text-2xl font-bold text-green-900 mt-1">
            {metrics.redeemed}
          </p>
        </div>
        <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
          <p className="text-sm font-medium text-yellow-600">Unredeemed</p>
          <p className="text-2xl font-bold text-yellow-900 mt-1">
            {metrics.unredeemed}
          </p>
        </div>
        <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
          <p className="text-sm font-medium text-purple-600">Total Value</p>
          <p className="text-2xl font-bold text-purple-900 mt-1">
            ${metrics.totalValue.toFixed(2)}
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3">
        <Button
          variant="primary"
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2"
        >
          <UserPlusIcon className="h-5 w-5" />
          Issue Reward
        </Button>
        <Button
          variant="secondary"
          onClick={() => navigate(`/redemptions?eventId=${eventId}`)}
          className="flex items-center gap-2"
        >
          <TicketIcon className="h-5 w-5" />
          View All Vouchers
        </Button>
      </div>

      {/* Issue Reward Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {modalStep === "form" && "Issue Reward"}
                {modalStep === "confirm" && "Confirm Reward Details"}
                {modalStep === "success" &&
                  (selectedCta?.reward_type === "voucher" || !selectedCta
                    ? "Voucher Created"
                    : "Reward Issued")}
              </h3>
              <button
                onClick={resetModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircleIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Step 1: Form */}
            {modalStep === "form" && (
              <>
                {/* Message when no CTAs with rewards exist */}
                {!hasCtasWithRewards && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-3">
                      <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <h4 className="text-sm font-medium text-yellow-800">
                          No Call to Action with Reward Configured
                        </h4>
                        <p className="text-sm text-yellow-700 mt-1">
                          To issue a reward, you must first create a Call to
                          Action with a reward type (voucher, URL offer, or
                          physical good) on the event's Calls to Action tab.
                        </p>
                        <button
                          type="button"
                          onClick={() => {
                            resetModal();
                            onGoToSettings();
                          }}
                          className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-yellow-800 hover:text-yellow-900 underline"
                        >
                          Go to Calls to Action
                          <ArrowRightIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {hasCtasWithRewards && (
                  <form onSubmit={handleProceedToConfirm}>
                    {/* CTA Selection */}
                    {ctasWithRewards.length > 0 && (
                      <div className="mb-4">
                        <label
                          htmlFor="cta"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Select Reward{" "}
                          {ctasWithRewards.length === 1 && "(Auto-selected)"}
                        </label>
                        {ctasWithRewards.length === 1 ? (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                            <div className="flex items-center justify-between">
                              <p className="font-medium text-green-800">
                                {ctasWithRewards[0].name ||
                                  ctasWithRewards[0].title}
                              </p>
                              <span
                                className={`px-2 py-0.5 rounded text-xs font-medium ${
                                  ctasWithRewards[0].reward_type === "voucher"
                                    ? "bg-purple-100 text-purple-800"
                                    : ctasWithRewards[0].reward_type ===
                                        "url_offer"
                                      ? "bg-blue-100 text-blue-800"
                                      : ctasWithRewards[0].reward_type ===
                                          "physical_good"
                                        ? "bg-orange-100 text-orange-800"
                                        : "bg-gray-100 text-gray-800"
                                }`}
                              >
                                {ctasWithRewards[0].reward_type === "voucher"
                                  ? "Voucher"
                                  : ctasWithRewards[0].reward_type ===
                                      "url_offer"
                                    ? "URL Offer"
                                    : ctasWithRewards[0].reward_type ===
                                        "physical_good"
                                      ? "Physical"
                                      : ctasWithRewards[0].reward_type ||
                                        "Other"}
                              </span>
                            </div>
                            {ctasWithRewards[0].reward_type === "voucher" && (
                              <p className="text-sm text-green-600 mt-1">
                                $
                                {ctasWithRewards[0].voucher_amount?.toFixed(
                                  2,
                                ) || "0.00"}{" "}
                                value
                              </p>
                            )}
                            {ctasWithRewards[0].reward_type === "url_offer" &&
                              ctasWithRewards[0].generated_url && (
                                <p className="text-sm text-blue-600 mt-1 truncate">
                                  {ctasWithRewards[0].generated_url}
                                </p>
                              )}
                          </div>
                        ) : (
                          <select
                            id="cta"
                            value={selectedCtaId}
                            onChange={(e) => setSelectedCtaId(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                          >
                            <option value="">Select a reward type...</option>
                            {ctasWithRewards.map((cta) => (
                              <option key={cta.id} value={cta.id}>
                                {cta.name || cta.title} -{" "}
                                {cta.reward_type === "voucher"
                                  ? `Voucher ($${cta.voucher_amount?.toFixed(2) || "0.00"})`
                                  : cta.reward_type === "url_offer"
                                    ? "URL Offer"
                                    : cta.reward_type === "physical_good"
                                      ? "Physical Item"
                                      : cta.reward_type || "Other"}
                              </option>
                            ))}
                          </select>
                        )}
                        {/* Show selected CTA details if multiple CTAs */}
                        {ctasWithRewards.length > 1 && selectedCta && (
                          <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-gray-700">
                                {selectedCta.name || selectedCta.title}
                              </span>
                              <span
                                className={`px-2 py-0.5 rounded text-xs font-medium ${
                                  selectedCta.reward_type === "voucher"
                                    ? "bg-purple-100 text-purple-800"
                                    : selectedCta.reward_type === "url_offer"
                                      ? "bg-blue-100 text-blue-800"
                                      : selectedCta.reward_type ===
                                          "physical_good"
                                        ? "bg-orange-100 text-orange-800"
                                        : "bg-gray-100 text-gray-800"
                                }`}
                              >
                                {selectedCta.reward_type === "voucher"
                                  ? "Voucher"
                                  : selectedCta.reward_type === "url_offer"
                                    ? "URL Offer"
                                    : selectedCta.reward_type ===
                                        "physical_good"
                                      ? "Physical"
                                      : selectedCta.reward_type || "Other"}
                              </span>
                            </div>
                            {selectedCta.reward_type === "voucher" && (
                              <p className="text-sm text-green-600 mt-1">
                                Value: $
                                {selectedCta.voucher_amount?.toFixed(2) ||
                                  "0.00"}
                              </p>
                            )}
                            {selectedCta.reward_type === "url_offer" &&
                              selectedCta.generated_url && (
                                <p className="text-sm text-blue-600 mt-1 truncate">
                                  URL: {selectedCta.generated_url}
                                </p>
                              )}
                            {selectedCta.reward_type === "physical_good" &&
                              selectedCta.fulfillment_instructions && (
                                <p className="text-sm text-orange-600 mt-1">
                                  {selectedCta.fulfillment_instructions}
                                </p>
                              )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Email Address with Lookup */}
                    <div className="mb-4">
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Email Address
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="email"
                          id="email"
                          value={createEmail}
                          onChange={(e) => {
                            setCreateEmail(e.target.value);
                            setLookupStatus("idle");
                            setLookupContact(null);
                          }}
                          placeholder="Enter email address"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                          required
                        />
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={handleEmailLookup}
                          disabled={
                            !createEmail.trim() || lookupStatus === "loading"
                          }
                        >
                          {lookupStatus === "loading" ? "..." : "Lookup"}
                        </Button>
                      </div>
                    </div>

                    {/* Contact Lookup Result */}
                    {lookupStatus === "found" && lookupContact && (
                      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-blue-900">
                              {lookupContact.first_name}{" "}
                              {lookupContact.last_name}
                            </p>
                            <p className="text-sm text-blue-700">
                              {lookupContact.email}
                            </p>
                          </div>
                          <span
                            className={`px-2 py-1 rounded text-xs font-medium text-white ${getMemberStatusBadge(lookupContact).color}`}
                          >
                            {getMemberStatusBadge(lookupContact).label}
                          </span>
                        </div>
                      </div>
                    )}

                    {lookupStatus === "not_found" && (
                      <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        <p className="text-sm text-gray-600">
                          No contact found with this email. A new contact record
                          will be created.
                        </p>
                      </div>
                    )}

                    {/* Recent Vouchers Warning */}
                    {recentVouchers.length > 0 && (
                      <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="flex items-start gap-2">
                          <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mt-0.5" />
                          <div>
                            <p className="font-medium text-yellow-800">
                              Recent vouchers for this contact
                            </p>
                            <p className="text-sm text-yellow-700">
                              {recentVouchers.length} voucher(s) issued in the
                              last 30 days:
                            </p>
                            <ul className="mt-1 text-xs text-yellow-600">
                              {recentVouchers.slice(0, 3).map((v) => (
                                <li key={v.id}>
                                  {v.voucher_code} - $
                                  {v.discount_value?.toFixed(2)} (
                                  {formatDate(v.created_at)})
                                </li>
                              ))}
                              {recentVouchers.length > 3 && (
                                <li>...and {recentVouchers.length - 3} more</li>
                              )}
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Issue Notes */}
                    <div className="mb-4">
                      <label
                        htmlFor="notes"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Notes (optional)
                      </label>
                      <textarea
                        id="notes"
                        value={issueNotes}
                        onChange={(e) => setIssueNotes(e.target.value)}
                        placeholder="Reason for issuing this voucher..."
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>

                    {createError && (
                      <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-600">{createError}</p>
                      </div>
                    )}

                    <div className="flex justify-end gap-3">
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={resetModal}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="primary"
                        disabled={!createEmail.trim()}
                      >
                        Continue
                      </Button>
                    </div>
                  </form>
                )}
              </>
            )}

            {/* Step 2: Confirmation */}
            {modalStep === "confirm" && (
              <div>
                <div className="space-y-4 mb-6">
                  {/* Summary */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">
                      Voucher Summary
                    </h4>
                    <dl className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Email:</dt>
                        <dd className="font-medium">{createEmail}</dd>
                      </div>
                      {lookupContact && (
                        <div className="flex justify-between">
                          <dt className="text-gray-500">Contact:</dt>
                          <dd className="font-medium">
                            {lookupContact.first_name} {lookupContact.last_name}
                          </dd>
                        </div>
                      )}
                      {selectedCta && (
                        <div className="flex justify-between">
                          <dt className="text-gray-500">CTA:</dt>
                          <dd className="font-medium">{selectedCta.title}</dd>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Value:</dt>
                        <dd className="font-medium text-green-700">
                          $
                          {(
                            selectedCta?.voucher_amount ||
                            event?.voucher_amount ||
                            0
                          ).toFixed(2)}
                        </dd>
                      </div>
                      {issueNotes && (
                        <div className="flex justify-between">
                          <dt className="text-gray-500">Notes:</dt>
                          <dd className="font-medium text-right max-w-[60%]">
                            {issueNotes}
                          </dd>
                        </div>
                      )}
                    </dl>
                  </div>

                  {/* Warning if recent vouchers */}
                  {recentVouchers.length > 0 && (
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800 font-medium">
                        This contact has {recentVouchers.length} voucher(s)
                        issued in the last 30 days.
                      </p>
                    </div>
                  )}

                  {/* Notification Options */}
                  {(selectedCta?.reward_type === "voucher" ||
                    !selectedCta?.reward_type) && (
                    <div className="bg-blue-50 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-3">
                        Send Notification
                      </h4>
                      <div className="space-y-2">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={sendVoucherEmail}
                            onChange={(e) =>
                              setSendVoucherEmail(e.target.checked)
                            }
                            className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">
                            Send voucher via Email
                          </span>
                        </label>
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={sendVoucherText}
                            onChange={(e) =>
                              setSendVoucherText(e.target.checked)
                            }
                            className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                            disabled
                          />
                          <span className="text-sm text-gray-400">
                            Send voucher via Text (coming soon)
                          </span>
                        </label>
                      </div>
                    </div>
                  )}
                </div>

                {createError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600">{createError}</p>
                  </div>
                )}

                <div className="flex justify-end gap-3">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setModalStep("form")}
                    disabled={isCreating}
                  >
                    Back
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    onClick={handleCreateVoucher}
                    disabled={isCreating}
                  >
                    {isCreating
                      ? "Processing..."
                      : selectedCta?.reward_type === "voucher" ||
                          !selectedCta?.reward_type
                        ? "Issue Voucher"
                        : selectedCta?.reward_type === "url_offer"
                          ? "Copy & Log Link"
                          : selectedCta?.reward_type === "physical_good"
                            ? "Log Distribution"
                            : "Issue Reward"}
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Success */}
            {modalStep === "success" && (
              <div className="text-center">
                <CheckCircleIconSolid className="h-16 w-16 text-green-500 mx-auto mb-4" />

                {/* Voucher Success */}
                {(selectedCta?.reward_type === "voucher" ||
                  !selectedCta?.reward_type) && (
                  <>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">
                      Voucher Created Successfully!
                    </h4>
                    <p className="text-gray-600 mb-4">
                      The voucher has been created and associated with the
                      event.
                    </p>
                    <div className="bg-gray-100 rounded-lg p-4 mb-6">
                      <p className="text-sm text-gray-600 mb-1">Voucher Code</p>
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-2xl font-mono font-bold text-gray-900">
                          {createdVoucherCode}
                        </span>
                        <button
                          onClick={handleCopyCode}
                          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded"
                          title="Copy to clipboard"
                        >
                          {copySuccess ? (
                            <CheckCircleIconSolid className="h-5 w-5 text-green-500" />
                          ) : (
                            <DocumentDuplicateIcon className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                      {copySuccess && (
                        <p className="text-xs text-green-600 mt-1">
                          Copied to clipboard!
                        </p>
                      )}
                    </div>
                  </>
                )}

                {/* URL Offer Success */}
                {selectedCta?.reward_type === "url_offer" && (
                  <>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">
                      Offer Link Ready!
                    </h4>
                    <p className="text-gray-600 mb-4">
                      Share this link with{" "}
                      {lookupContact?.first_name || "the contact"}.
                    </p>
                    <div className="bg-blue-50 rounded-lg p-4 mb-6">
                      <p className="text-sm text-blue-600 mb-1">Offer URL</p>
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-sm font-mono text-blue-900 break-all">
                          {createdVoucherCode}
                        </span>
                        <button
                          onClick={handleCopyCode}
                          className="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-100 rounded flex-shrink-0"
                          title="Copy to clipboard"
                        >
                          {copySuccess ? (
                            <CheckCircleIconSolid className="h-5 w-5 text-green-500" />
                          ) : (
                            <DocumentDuplicateIcon className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                      {copySuccess && (
                        <p className="text-xs text-green-600 mt-1">
                          Copied to clipboard!
                        </p>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mb-4">
                      Interaction logged to contact's record.
                    </p>
                  </>
                )}

                {/* Physical Good Success */}
                {selectedCta?.reward_type === "physical_good" && (
                  <>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">
                      Item Distribution Logged!
                    </h4>
                    <p className="text-gray-600 mb-4">
                      The {selectedCta?.name || "item"} has been marked as
                      distributed to {lookupContact?.first_name}{" "}
                      {lookupContact?.last_name}.
                    </p>
                    <div className="bg-orange-50 rounded-lg p-4 mb-6">
                      <p className="text-sm text-orange-800">
                        <strong>Item:</strong>{" "}
                        {selectedCta?.name || selectedCta?.title}
                      </p>
                      <p className="text-sm text-orange-800">
                        <strong>Status:</strong> Handed out at event
                      </p>
                      {issueNotes && (
                        <p className="text-sm text-orange-600 mt-2">
                          <strong>Notes:</strong> {issueNotes}
                        </p>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mb-4">
                      Interaction logged to contact's record.
                    </p>
                  </>
                )}

                <Button type="button" variant="primary" onClick={resetModal}>
                  Done
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex items-center gap-2 border-b border-gray-200">
        <FunnelIcon className="h-5 w-5 text-gray-400" />
        <div className="flex gap-1">
          {[
            { id: "all" as const, label: "All", count: metrics.total },
            {
              id: "redeemed" as const,
              label: "Redeemed",
              count: metrics.redeemed,
            },
            {
              id: "unredeemed" as const,
              label: "Unredeemed",
              count: metrics.unredeemed,
            },
          ].map(({ id, label, count }) => (
            <button
              key={id}
              onClick={() => setStatusFilter(id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                statusFilter === id
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {label} ({count})
            </button>
          ))}
        </div>
      </div>

      {/* Vouchers List */}
      {filteredVouchers.length === 0 ? (
        <div className="text-center py-12">
          <TicketIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No vouchers found
          </h3>
          <p className="text-gray-500">
            {statusFilter === "all"
              ? "No vouchers have been associated with this event yet."
              : `No ${statusFilter} vouchers.`}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredVouchers.map((voucher) => (
            <div
              key={voucher.id}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 flex-1">
                  {/* Status Icon */}
                  <div>
                    {voucher.is_redeemed ? (
                      <CheckCircleIconSolid className="h-8 w-8 text-green-500" />
                    ) : (
                      <TicketIcon className="h-8 w-8 text-gray-300" />
                    )}
                  </div>

                  {/* Voucher Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h4 className="text-base font-semibold text-gray-900 font-mono">
                        {voucher.voucher_code}
                      </h4>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          voucherStatusLabel(voucher) === "Redeemed"
                            ? "bg-green-100 text-green-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {voucherStatusLabel(voucher)}
                      </span>
                    </div>

                    <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                      {voucher.email && <span>{voucher.email}</span>}
                      {voucher.voucher_name && (
                        <span className="font-medium text-gray-700">
                          {voucher.voucher_name}
                        </span>
                      )}
                    </div>

                    {/* Value and Dates */}
                    <div className="flex items-center gap-4 mt-2 text-sm">
                      <span className="font-semibold text-green-700">
                        $
                        {(voucher.is_redeemed
                          ? (voucher.applied_amount ?? voucher.discount_value)
                          : voucher.discount_value
                        )?.toFixed(2) || "0.00"}
                      </span>
                      {voucher.start_date && (
                        <span className="text-gray-500">
                          Valid from: {formatDate(voucher.start_date)}
                        </span>
                      )}
                      {voucher.end_date && (
                        <span className="text-gray-500">
                          Until: {formatDate(voucher.end_date)}
                        </span>
                      )}
                    </div>

                    {/* Redemption Details */}
                    {voucher.is_redeemed && voucher.redeemed_at && (
                      <div className="mt-2 text-xs text-gray-400">
                        Redeemed: {formatDate(voucher.redeemed_at)}
                        {voucher.redeemed_by && ` by ${voucher.redeemed_by}`}
                        {voucher.redemption_source &&
                          ` via ${voucher.redemption_source}`}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ListsTab: React.FC<{ eventId: string }> = () => {
  return (
    <div className="text-center py-12">
      <ClipboardDocumentListIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
      <h3 className="text-lg font-medium text-gray-900 mb-2">Lists</h3>
      <p className="text-gray-500 mb-4">
        Contact lists associated with this event will be shown here
      </p>
    </div>
  );
};

export default EventDetail;
